/**
 * Hilfe-Inhalte für Skeena
 *
 * Um neue Hilfe-Artikel hinzuzufügen:
 * 1. Fügen Sie eine neue Kategorie zu HelpCategory hinzu (falls nötig)
 * 2. Erstellen Sie ein neues HelpArticle-Objekt im helpArticles Array
 * 3. Das System aktualisiert automatisch die Suche und Navigation
 */

export interface HelpArticle {
  id: string;
  category: HelpCategory;
  title: string;
  description: string;
  content: string; // Unterstützt HTML für bessere Formatierung
  keywords: string[]; // Für die Suchfunktion
  order: number; // Sortierreihenfolge innerhalb der Kategorie
  lastUpdated?: string; // Datum des letzten Updates
}

export type HelpCategory =
  | 'erste-schritte'
  | 'tickets'
  | 'kalender'
  | 'fristen'
  | 'emails'
  | 'adressen'
  | 'dokumente'
  | 'projekte'
  | 'workflows'
  | 'vorlagen'
  | 'textbausteine'
  | 'assets'
  | 'einstellungen'
  | 'administration'
  | 'tipps-tricks';

export interface CategoryInfo {
  id: HelpCategory;
  name: string;
  icon: string;
  description: string;
}

export const categories: CategoryInfo[] = [
  {
    id: 'erste-schritte',
    name: 'Erste Schritte',
    icon: 'RocketLaunch',
    description: 'Grundlegende Informationen für den Einstieg in Skeena'
  },
  {
    id: 'tickets',
    name: 'Tickets',
    icon: 'Assignment',
    description: 'Verwaltung von Aufgaben und Tickets'
  },
  {
    id: 'kalender',
    name: 'Kalender',
    icon: 'CalendarMonth',
    description: 'Übersicht und Verwaltung von Ticket-Fristen im Kalender'
  },
  {
    id: 'fristen',
    name: 'Fristen (Treuhand)',
    icon: 'EventNote',
    description: 'Fristenverwaltung für Treuhand-Aufgaben'
  },
  {
    id: 'emails',
    name: 'E-Mails',
    icon: 'Email',
    description: 'E-Mail-Verwaltung und Integration'
  },
  {
    id: 'adressen',
    name: 'Adressen',
    icon: 'People',
    description: 'Kontaktverwaltung und Adressbuch'
  },
  {
    id: 'dokumente',
    name: 'Dokumente',
    icon: 'Description',
    description: 'Dokumentenerstellung und -verwaltung'
  },
  {
    id: 'projekte',
    name: 'Projekte',
    icon: 'Folder',
    description: 'Projektverwaltung und Meilensteine'
  },
  {
    id: 'workflows',
    name: 'Workflows',
    icon: 'AccountTree',
    description: 'Workflow-Vorlagen und automatisierte Arbeitsabläufe'
  },
  {
    id: 'vorlagen',
    name: 'Vorlagen',
    icon: 'Create',
    description: 'Dokumentvorlagen erstellen und verwenden'
  },
  {
    id: 'textbausteine',
    name: 'Textbausteine',
    icon: 'TextFields',
    description: 'Wiederverwendbare Textbausteine verwalten'
  },
  {
    id: 'assets',
    name: 'Asset-Verwaltung',
    icon: 'Computer',
    description: 'IT-Asset-Management mit Snipe-IT Integration'
  },
  {
    id: 'einstellungen',
    name: 'Einstellungen',
    icon: 'Settings',
    description: 'Persönliche und System-Einstellungen'
  },
  {
    id: 'administration',
    name: 'Administration',
    icon: 'AdminPanelSettings',
    description: 'Mandanten- und Benutzerverwaltung (nur für Admins)'
  },
  {
    id: 'tipps-tricks',
    name: 'Tipps & Tricks',
    icon: 'Lightbulb',
    description: 'Nützliche Tipps für die effiziente Nutzung von Skeena'
  }
];

export const helpArticles: HelpArticle[] = [
  // Erste Schritte
  {
    id: 'willkommen',
    category: 'erste-schritte',
    title: 'Willkommen bei Skeena',
    description: 'Eine Einführung in die Skeena-Plattform',
    keywords: ['start', 'einführung', 'übersicht', 'willkommen'],
    order: 1,
    content:`
      <h3>Willkommen bei Skeena!</h3>
      <p>Skeena ist Ihre KI-gestützte Lösung für Brief- und Mail-Verwaltung. Mit Skeena können Sie:</p>
      <ul>
        <li><strong>Tickets verwalten:</strong> Erstellen und verfolgen Sie Aufgaben mit Fristen und Prioritäten</li>
        <li><strong>E-Mails organisieren:</strong> Automatische Kategorisierung und intelligente Antwortvorschläge</li>
        <li><strong>Dokumente erstellen:</strong> Nutzen Sie Vorlagen und KI-Unterstützung</li>
        <li><strong>Kontakte verwalten:</strong> Zentrales Adressbuch mit allen wichtigen Informationen</li>
        <li><strong>Projekte koordinieren:</strong> Strukturierte Projektverwaltung mit Meilensteinen</li>
      </ul>
      <p><strong>Navigation:</strong> Nutzen Sie das Menü auf der linken Seite, um zwischen den verschiedenen Bereichen zu wechseln.</p>
      <p><strong>Hilfe:</strong> Klicken Sie auf das Fragezeichen-Symbol oben rechts, um jederzeit auf diese Hilfe zuzugreifen.</p>
   `
  },
  {
    id: 'dashboard-uebersicht',
    category: 'erste-schritte',
    title: 'Dashboard-Übersicht',
    description: 'Die wichtigsten Informationen auf einen Blick',
    keywords: ['dashboard', 'übersicht', 'statistiken', 'start'],
    order: 2,
    content:`
      <h3>Das Dashboard</h3>
      <p>Das Dashboard ist Ihre zentrale Anlaufstelle und zeigt die wichtigsten Informationen auf einen Blick:</p>
      <ul>
        <li><strong>Statistiken:</strong> Anzahl offener Tickets, ungelesener E-Mails, Adressen und Dokumente</li>
        <li><strong>Letzte Aktivitäten:</strong> Chronologische Übersicht der neuesten Ereignisse</li>
        <li><strong>Schnellzugriff:</strong> Direkte Links zu häufig genutzten Funktionen</li>
      </ul>
      <p><strong>Tipp:</strong> Das Dashboard aktualisiert sich automatisch bei neuen Aktivitäten.</p>
   `
  },

  // Tickets
  {
    id: 'tickets-erstellen',
    category: 'tickets',
    title: 'Tickets erstellen',
    description: 'So erstellen Sie neue Tickets',
    keywords: ['ticket', 'erstellen', 'neu', 'aufgabe'],
    order: 1,
    content:`
      <h3>Ein neues Ticket erstellen</h3>
      <ol>
        <li>Klicken Sie auf <strong>"Tickets"</strong> im Menü</li>
        <li>Klicken Sie auf den <strong>"Neues Ticket"</strong> Button</li>
        <li>Füllen Sie die erforderlichen Felder aus:
          <ul>
            <li><strong>Titel:</strong> Kurze, prägnante Beschreibung</li>
            <li><strong>Was:</strong> Detaillierte Aufgabenbeschreibung</li>
            <li><strong>Wie:</strong> (Optional) Vorgehensweise zur Lösung</li>
            <li><strong>Wann:</strong> Frist für die Erledigung</li>
            <li><strong>Status:</strong> Offen, In Bearbeitung oder Erledigt</li>
            <li><strong>Priorität:</strong> Niedrig, Mittel oder Hoch</li>
          </ul>
        </li>
        <li>Klicken Sie auf <strong>"Speichern"</strong></li>
      </ol>
      <p><strong>Tipp:</strong> Tickets können automatisch aus E-Mails erstellt werden!</p>
   `
  },
  {
    id: 'tickets-verwalten',
    category: 'tickets',
    title: 'Tickets verwalten',
    description: 'Tickets bearbeiten, filtern und organisieren',
    keywords: ['ticket', 'bearbeiten', 'filtern', 'status', 'priorität'],
    order: 2,
    content:`
      <h3>Tickets verwalten</h3>
      <p><strong>Status ändern:</strong></p>
      <ul>
        <li>Klicken Sie auf das Status-Dropdown in der Ticket-Zeile</li>
        <li>Wählen Sie den neuen Status: Offen, In Bearbeitung oder Erledigt</li>
        <li>Der Status wird automatisch gespeichert</li>
      </ul>
      <p><strong>Tickets filtern:</strong></p>
      <ul>
        <li>Nutzen Sie den Status-Filter oberhalb der Tabelle</li>
        <li>Zeigen Sie nur relevante Tickets an</li>
      </ul>
      <p><strong>Tickets bearbeiten:</strong></p>
      <ul>
        <li>Klicken Sie auf das Stift-Symbol in der Aktions-Spalte</li>
        <li>Ändern Sie die gewünschten Felder</li>
        <li>Speichern Sie die Änderungen</li>
      </ul>
      <p><strong>Tickets löschen:</strong></p>
      <ul>
        <li>Klicken Sie auf das Papierkorb-Symbol</li>
        <li>Bestätigen Sie die Löschung</li>
      </ul>
   `
  },
  {
    id: 'tickets-automatisierung',
    category: 'tickets',
    title: 'Ticket-Automatisierung',
    description: 'Automatische Ticket-Erstellung und Wiederholung',
    keywords: ['automatisierung', 'wiederholung', 'n8n', 'automation'],
    order: 3,
    content:`
      <h3>Ticket-Automatisierung</h3>
      <p><strong>Automatische Erstellung aus E-Mails:</strong></p>
      <ul>
        <li>Skeena kann automatisch Tickets aus eingehenden E-Mails erstellen</li>
        <li>Die KI analysiert den E-Mail-Inhalt und füllt die Ticket-Felder automatisch</li>
        <li>Solche Tickets werden als "automatisch erstellt" markiert</li>
      </ul>
      <p><strong>Wiederkehrende Tickets:</strong></p>
      <ul>
        <li>Aktivieren Sie die Wiederholung beim Erstellen eines Tickets</li>
        <li>Wählen Sie das Intervall: Täglich, Wöchentlich, Monatlich</li>
        <li>Optional: Begrenzen Sie die Anzahl der Wiederholungen</li>
        <li>Das System erstellt automatisch neue Tickets im gewählten Intervall</li>
      </ul>
      <p><strong>N8N-Integration:</strong></p>
      <ul>
        <li>Tickets können mit N8N-Workflows verbunden werden</li>
        <li>Automatische Ausführung von Aktionen bei Ticket-Ereignissen</li>
      </ul>
   `
  },
  {
    id: 'tickets-dokumente-emails',
    category: 'tickets',
    title: 'Dokumente & E-Mails zu Tickets',
    description: 'Dokumente und E-Mails mit Tickets verknüpfen',
    keywords: ['dokument', 'email', 'verknüpfen', 'anhang', 'zuordnen'],
    order: 4,
    content:`
      <h3>Dokumente & E-Mails zu Tickets</h3>
      <p>Verknüpfen Sie relevante Dokumente und E-Mails direkt mit Ihren Tickets für bessere Organisation und schnelleren Zugriff.</p>

      <p><strong>Dokumente zu Tickets hinzufügen:</strong></p>
      <ul>
        <li>Öffnen Sie ein Ticket durch Klick auf das Stift-Symbol</li>
        <li>Wechseln Sie zum <strong>"Dokumente"</strong> Tab</li>
        <li>Klicken Sie auf <strong>"Dokument hinzufügen"</strong></li>
        <li>Suchen Sie das gewünschte Dokument in der Autocomplete-Auswahl</li>
        <li>Das Dokument wird sofort mit dem Ticket verknüpft</li>
      </ul>

      <p><strong>Dokumente verwalten:</strong></p>
      <ul>
        <li><strong>Herunterladen:</strong> Klicken Sie auf das Download-Symbol</li>
        <li><strong>Bearbeiten:</strong> Klicken Sie auf das Stift-Symbol - Sie werden zum Bearbeitungsmodus weitergeleitet</li>
        <li><strong>Verknüpfung entfernen:</strong> Klicken Sie auf das Papierkorb-Symbol</li>
      </ul>

      <p><strong>E-Mails zu Tickets hinzufügen:</strong></p>
      <ul>
        <li>Öffnen Sie ein Ticket durch Klick auf das Stift-Symbol</li>
        <li>Wechseln Sie zum <strong>"E-Mail"</strong> Tab</li>
        <li>Klicken Sie auf <strong>"E-Mail hinzufügen"</strong></li>
        <li>Suchen Sie die gewünschte E-Mail (es werden nur aktive E-Mails angezeigt)</li>
        <li>Die E-Mail wird mit dem Ticket verknüpft</li>
      </ul>

      <p><strong>E-Mail-Funktionen:</strong></p>
      <ul>
        <li><strong>E-Mail öffnen:</strong> Klicken Sie auf das Öffnen-Symbol, um die vollständige E-Mail anzuzeigen</li>
        <li><strong>Textvorschau:</strong> Die ersten 500 Zeichen werden direkt angezeigt</li>
        <li><strong>Verknüpfung entfernen:</strong> Klicken Sie auf "Verknüpfung entfernen"</li>
      </ul>

      <p><strong>Vorteile:</strong></p>
      <ul>
        <li>Alle relevanten Informationen an einem Ort</li>
        <li>Schneller Zugriff auf verknüpfte Dokumente und E-Mails</li>
        <li>Bessere Nachvollziehbarkeit von Aufgaben</li>
        <li>Kein Suchen nach zugehörigen Dokumenten oder E-Mails</li>
      </ul>

      <p><strong>Wichtig:</strong> Pro Ticket kann eine E-Mail verknüpft werden, aber beliebig viele Dokumente.</p>
   `
  },

  // Kalender
  {
    id: 'kalender-uebersicht',
    category: 'kalender',
    title: 'Kalender-Übersicht',
    description: 'Tickets im Kalender visualisieren',
    keywords: ['kalender', 'übersicht', 'frist', 'datum', 'termine'],
    order: 1,
    content:`
      <h3>Der Ticket-Kalender</h3>
      <p>Der Kalender zeigt alle Tickets mit ihren Fälligkeitsdaten (Wann-Feld) übersichtlich an.</p>
      <p><strong>Ansichten wechseln:</strong></p>
      <ul>
        <li><strong>Monat:</strong> Übersicht über den ganzen Monat</li>
        <li><strong>Woche:</strong> Detaillierte Wochenansicht</li>
        <li><strong>Tag:</strong> Alle Tickets eines Tages</li>
        <li><strong>Agenda:</strong> Listenansicht der anstehenden Tickets</li>
      </ul>
      <p><strong>Farbcodierung:</strong></p>
      <ul>
        <li><span style="color: #4caf50;">●</span> <strong>Grün:</strong> Erledigt</li>
        <li><span style="color: #2196f3;">●</span> <strong>Blau:</strong> In Bearbeitung</li>
        <li><span style="color: #ff9800;">●</span> <strong>Orange:</strong> Offen</li>
        <li><span style="color: #f44336;">●</span> <strong>Rot:</strong> Überfällig</li>
      </ul>
      <p><strong>Navigation:</strong></p>
      <ul>
        <li>Nutzen Sie die Pfeile zum Vor- und Zurückblättern</li>
        <li>Klicken Sie auf "Heute" um zum aktuellen Datum zurückzukehren</li>
      </ul>
   `
  },
  {
    id: 'kalender-tickets-anzeigen',
    category: 'kalender',
    title: 'Tickets im Kalender anzeigen',
    description: 'Ticket-Details öffnen und Status ändern',
    keywords: ['kalender', 'ticket', 'details', 'status', 'anzeigen'],
    order: 2,
    content:`
      <h3>Tickets im Kalender anzeigen</h3>
      <p><strong>Ticket-Details öffnen:</strong></p>
      <ol>
        <li>Klicken Sie auf ein Ticket im Kalender</li>
        <li>Ein Dialog mit allen Details öffnet sich:
          <ul>
            <li>Titel und vollständige Beschreibung</li>
            <li>Aktueller Status und Priorität</li>
            <li>Frist und Erstellungsdatum</li>
            <li>Vorgehensweise (falls vorhanden)</li>
          </ul>
        </li>
      </ol>
      <p><strong>Status direkt ändern:</strong></p>
      <ul>
        <li>Im Ticket-Dialog finden Sie ein Status-Dropdown</li>
        <li>Wählen Sie den neuen Status aus</li>
        <li>Die Änderung wird sofort gespeichert</li>
        <li>Der Kalender aktualisiert sich automatisch (Farbänderung)</li>
      </ul>
      <p><strong>Filtern nach Status:</strong></p>
      <ul>
        <li>Nutzen Sie den Status-Filter oben rechts</li>
        <li>Zeigen Sie nur Tickets mit bestimmtem Status an</li>
        <li>Perfekt für fokussiertes Arbeiten</li>
      </ul>
   `
  },
  {
    id: 'kalender-termine-versenden',
    category: 'kalender',
    title: 'Kalender-Termine versenden',
    description: 'iCal-Termine per E-Mail versenden',
    keywords: ['kalender', 'termin', 'ical', 'email', 'versenden', 'einladung'],
    order: 3,
    content:`
      <h3>Kalender-Termine versenden</h3>
      <p>Sie können Ticket-Fristen als Kalender-Einladungen per E-Mail versenden.</p>
      <p><strong>Vorgehensweise:</strong></p>
      <ol>
        <li>Gehen Sie zur Ticket-Übersicht</li>
        <li>Klicken Sie auf das Kalender-Symbol beim gewünschten Ticket</li>
        <li>Geben Sie die E-Mail-Adresse des Empfängers ein</li>
        <li>Optional: Fügen Sie eine Nachricht hinzu</li>
        <li>Klicken Sie auf "Senden"</li>
      </ol>
      <p><strong>Was erhält der Empfänger?</strong></p>
      <ul>
        <li>Eine E-Mail mit .ics-Datei im Anhang</li>
        <li>Der Termin kann direkt in Outlook, Google Calendar, etc. importiert werden</li>
        <li>Enthält Titel, Beschreibung und Frist des Tickets</li>
      </ul>
      <p><strong>Voraussetzung:</strong> Das Ticket muss ein gesetztes Fälligkeitsdatum (Wann-Feld) haben.</p>
   `
  },
  {
    id: 'kalender-caldav',
    category: 'kalender',
    title: 'CalDAV-Synchronisation',
    description: 'Ticket-Fristen mit externen Kalendern synchronisieren (Outlook, Apple, Google)',
    keywords: ['kalender', 'caldav', 'sync', 'synchronisation', 'outlook', 'google', 'apple', 'extern', 'integration'],
    order: 4,
    lastUpdated: '2025-11-29',
    content:`
      <h3>CalDAV-Synchronisation</h3>
      <p>Mit der CalDAV-Integration werden Ihre Ticket-Fristen automatisch mit externen Kalendern synchronisiert. So haben Sie alle Fristen auch in Outlook, Apple Calendar oder Google Calendar im Blick.</p>

      <p><strong>Unterstützte Kalender:</strong></p>
      <ul>
        <li><strong>Microsoft 365 / Outlook.com:</strong> Vollständige Unterstützung</li>
        <li><strong>Apple iCloud Calendar:</strong> Vollständige Unterstützung</li>
        <li><strong>Google Calendar:</strong> Vollständige Unterstützung</li>
        <li><strong>Andere CalDAV-Server:</strong> Nextcloud, Synology, etc.</li>
      </ul>

      <p><strong>CalDAV einrichten:</strong></p>
      <ol>
        <li>Gehen Sie zu <strong>Einstellungen &rarr; CalDAV-Synchronisation</strong></li>
        <li>Geben Sie folgende Informationen ein:
          <ul>
            <li><strong>CalDAV-URL:</strong> Die Server-URL Ihres Kalender-Anbieters</li>
            <li><strong>Benutzername:</strong> Ihre E-Mail-Adresse oder Benutzername</li>
            <li><strong>Passwort:</strong> Ihr Passwort oder App-Passwort</li>
          </ul>
        </li>
        <li>Klicken Sie auf <strong>"Verbindung testen"</strong></li>
        <li>Speichern Sie die Einstellungen</li>
      </ol>

      <p><strong>Wichtige Hinweise zu App-Passwörtern:</strong></p>
      <ul>
        <li><strong>Microsoft 365:</strong> Wenn 2FA aktiviert ist, erstellen Sie ein App-Passwort unter account.microsoft.com</li>
        <li><strong>Google:</strong> Erstellen Sie ein App-Passwort unter myaccount.google.com/apppasswords</li>
        <li><strong>iCloud:</strong> Erstellen Sie ein App-Passwort unter appleid.apple.com</li>
      </ul>

      <p><strong>CalDAV-URLs der wichtigsten Anbieter:</strong></p>
      <ul>
        <li><strong>Microsoft 365:</strong> <code>https://outlook.office365.com/caldav/</code></li>
        <li><strong>Google Calendar:</strong> <code>https://www.google.com/calendar/dav/</code></li>
        <li><strong>iCloud:</strong> <code>https://caldav.icloud.com/</code></li>
      </ul>

      <p><strong>Automatische Synchronisation:</strong></p>
      <ul>
        <li>Ticket-Fristen werden automatisch als Kalender-Ereignisse erstellt</li>
        <li>Änderungen am Ticket werden automatisch synchronisiert</li>
        <li>Manuelle Synchronisation über den Button "Jetzt synchronisieren"</li>
      </ul>
   `
  },

  // E-Mails
  {
    id: 'emails-konten',
    category: 'emails',
    title: 'E-Mail-Konten einrichten',
    description: 'IMAP/SMTP-Konten hinzufügen und verwalten',
    keywords: ['email', 'konto', 'imap', 'smtp', 'einrichten'],
    order: 1,
    content:`
      <h3>E-Mail-Konten einrichten</h3>
      <p>Verbinden Sie Ihre E-Mail-Konten mit Skeena für nahtlose Integration.</p>
      <p><strong>Neues Konto hinzufügen:</strong></p>
      <ol>
        <li>Gehen Sie zu <strong>Einstellungen → E-Mail-Konten</strong></li>
        <li>Klicken Sie auf "Neues Konto"</li>
        <li>Geben Sie folgende Informationen ein:
          <ul>
            <li><strong>E-Mail-Adresse:</strong> Ihre vollständige E-Mail-Adresse</li>
            <li><strong>IMAP-Server:</strong> Server zum Empfangen (z.B. imap.gmail.com)</li>
            <li><strong>IMAP-Port:</strong> Meist 993 (SSL) oder 143</li>
            <li><strong>SMTP-Server:</strong> Server zum Senden (z.B. smtp.gmail.com)</li>
            <li><strong>SMTP-Port:</strong> Meist 587 (TLS) oder 465 (SSL)</li>
            <li><strong>Benutzername & Passwort:</strong> Ihre Zugangsdaten</li>
          </ul>
        </li>
        <li>Klicken Sie auf "Speichern und testen"</li>
      </ol>
      <p><strong>Tipp:</strong> Aktivieren Sie das Konto als Standard-Konto für ausgehende E-Mails.</p>
   `
  },
  {
    id: 'emails-abrufen',
    category: 'emails',
    title: 'E-Mails abrufen und lesen',
    description: 'E-Mails empfangen und verwalten',
    keywords: ['email', 'abrufen', 'lesen', 'empfangen', 'inbox'],
    order: 2,
    content:`
      <h3>E-Mails abrufen und lesen</h3>
      <p><strong>E-Mails manuell abrufen:</strong></p>
      <ul>
        <li>Gehen Sie zur E-Mail-Übersicht</li>
        <li>Klicken Sie auf "E-Mails abrufen"</li>
        <li>Wählen Sie ein Konto oder "Alle Konten"</li>
        <li>Optional: Legen Sie die maximale Anzahl abzurufender E-Mails fest</li>
      </ul>
      <p><strong>E-Mail lesen:</strong></p>
      <ul>
        <li>Klicken Sie auf eine E-Mail in der Liste</li>
        <li>Die E-Mail wird automatisch als gelesen markiert</li>
        <li>Sie sehen Absender, Betreff, Datum und den vollständigen Inhalt</li>
        <li>Anhänge können heruntergeladen werden</li>
      </ul>
      <p><strong>E-Mail-Status:</strong></p>
      <ul>
        <li><strong>Ungelesen:</strong> Fettgedruckt dargestellt</li>
        <li><strong>Gelesen:</strong> Normale Schriftart</li>
        <li><strong>Beantwortet:</strong> Mit Symbol markiert</li>
      </ul>
   `
  },
  {
    id: 'emails-beantworten',
    category: 'emails',
    title: 'E-Mails beantworten',
    description: 'Antworten verfassen mit KI-Unterstützung',
    keywords: ['email', 'antworten', 'reply', 'ki', 'vorschlag'],
    order: 3,
    content:`
      <h3>E-Mails beantworten</h3>
      <p><strong>Manuelle Antwort:</strong></p>
      <ol>
        <li>Öffnen Sie die E-Mail</li>
        <li>Klicken Sie auf "Antworten"</li>
        <li>Verfassen Sie Ihre Nachricht</li>
        <li>Optional: Wählen Sie eine Signatur</li>
        <li>Klicken Sie auf "Senden"</li>
      </ol>
      <p><strong>KI-Antwortvorschläge:</strong></p>
      <ul>
        <li>Klicken Sie auf "KI-Vorschläge generieren"</li>
        <li>Die KI analysiert die E-Mail und erstellt passende Antworten</li>
        <li>Wählen Sie einen Vorschlag aus oder passen Sie ihn an</li>
        <li>Spart Zeit und sorgt für professionelle Antworten</li>
      </ul>
      <p><strong>Weiterleiten:</strong></p>
      <ul>
        <li>Klicken Sie auf "Weiterleiten"</li>
        <li>Geben Sie die Empfänger-Adresse(n) ein</li>
        <li>Fügen Sie optional eine Nachricht hinzu</li>
        <li>Klicken Sie auf "Senden"</li>
      </ul>
   `
  },
  {
    id: 'emails-analyse',
    category: 'emails',
    title: 'E-Mail-Analyse mit KI',
    description: 'Automatische Kategorisierung und Zusammenfassung von E-Mails',
    keywords: ['email', 'ki', 'analyse', 'kategorisierung', 'zusammenfassung', 'ai'],
    order: 4,
    lastUpdated: '2025-01-14',
    content:`
      <h3>E-Mail-Analyse mit KI</h3>
      <p>Skeena nutzt künstliche Intelligenz, um E-Mails automatisch zu analysieren, zu kategorisieren und zusammenzufassen. Dies spart Zeit und hilft bei der Organisation Ihrer Kommunikation.</p>

      <p><strong>Automatische Kategorisierung:</strong></p>
      <ul>
        <li>Die KI analysiert eingehende E-Mails und ordnet sie automatisch Kategorien zu</li>
        <li>Kategorien: Anfrage, Beschwerde, Information, Auftrag, Rechnung, etc.</li>
        <li>Kategorien werden in der E-Mail-Liste angezeigt</li>
        <li>Filtern Sie E-Mails nach Kategorien für bessere Übersicht</li>
      </ul>

      <p><strong>Automatische Zusammenfassung:</strong></p>
      <ul>
        <li>Lange E-Mails werden automatisch zusammengefasst</li>
        <li>Die Zusammenfassung enthält die wichtigsten Punkte in 2-3 Sätzen</li>
        <li>Schneller Überblick ohne die ganze E-Mail lesen zu müssen</li>
        <li>Perfekt für E-Mail-Triage am Morgen</li>
      </ul>

      <p><strong>Manuelle Analyse auslösen:</strong></p>
      <ol>
        <li>Öffnen Sie eine E-Mail</li>
        <li>Klicken Sie auf <strong>"E-Mail analysieren"</strong></li>
        <li>Die KI analysiert die E-Mail und zeigt:
          <ul>
            <li>Kategorie</li>
            <li>Zusammenfassung</li>
            <li>Vorgeschlagene Aktionen (z.B. Ticket erstellen)</li>
          </ul>
        </li>
      </ol>

      <p><strong>Ticket-Analyse:</strong></p>
      <ul>
        <li>Die KI kann analysieren, ob aus einer E-Mail ein Ticket erstellt werden sollte</li>
        <li>Bei positiver Bewertung werden automatisch Ticket-Felder vorausgefüllt:
          <ul>
            <li>Titel (aus Betreff extrahiert)</li>
            <li>Was (Aufgabenbeschreibung)</li>
            <li>Wie (Lösungsvorschlag)</li>
            <li>Wann (vorgeschlagene Frist)</li>
            <li>Priorität (basierend auf Dringlichkeit)</li>
          </ul>
        </li>
        <li>Sie können die Vorschläge anpassen und mit einem Klick das Ticket erstellen</li>
      </ul>

      <p><strong>Voraussetzungen:</strong></p>
      <ul>
        <li>Ein LLM-Provider muss konfiguriert sein (OpenAI, Anthropic, etc.)</li>
        <li>Ihr Benutzer benötigt LLM-Zugang (wird vom Administrator zugewiesen)</li>
        <li>Einstellungen → LLM-Provider muss konfiguriert sein</li>
      </ul>

      <p><strong>Tipps:</strong></p>
      <ul>
        <li><strong>Automatische Analyse:</strong> Aktivieren Sie die automatische Analyse für eingehende E-Mails</li>
        <li><strong>Datenschutz:</strong> E-Mail-Inhalte werden nur zur Analyse genutzt und nicht gespeichert</li>
        <li><strong>Genauigkeit:</strong> Die KI-Analyse ist ca. 90% genau - prüfen Sie wichtige Entscheidungen</li>
        <li><strong>Mehrsprachig:</strong> Die Analyse funktioniert in Deutsch, Englisch und vielen weiteren Sprachen</li>
      </ul>
   `
  },
  {
    id: 'emails-signaturen',
    category: 'emails',
    title: 'E-Mail-Signaturen',
    description: 'Signaturen erstellen und verwalten',
    keywords: ['email', 'signatur', 'signature', 'fusszeile', 'footer'],
    order: 5,
    lastUpdated: '2025-01-14',
    content:`
      <h3>E-Mail-Signaturen</h3>
      <p>E-Mail-Signaturen werden automatisch an ausgehende E-Mails angehängt und enthalten üblicherweise Ihre Kontaktdaten und rechtliche Hinweise.</p>

      <p><strong>Neue Signatur erstellen:</strong></p>
      <ol>
        <li>Gehen Sie zu <strong>Einstellungen → E-Mail-Signaturen</strong></li>
        <li>Klicken Sie auf <strong>"Neue Signatur"</strong></li>
        <li>Füllen Sie die Felder aus:
          <ul>
            <li><strong>Name:</strong> Interner Name der Signatur (z.B. "Standard", "Geschäftlich")</li>
            <li><strong>Text-Inhalt:</strong> Signatur als reiner Text (für E-Mail-Clients ohne HTML)</li>
            <li><strong>HTML-Inhalt:</strong> Optional formatierte Signatur mit Farben, Links, etc.</li>
            <li><strong>Als Standard festlegen:</strong> Diese Signatur wird automatisch verwendet</li>
          </ul>
        </li>
        <li>Klicken Sie auf <strong>"Speichern"</strong></li>
      </ol>

      <p><strong>Signatur-Beispiel (Text):</strong></p>
      <code style="display: block; white-space: pre-wrap; background: #f5f5f5; padding: 10px; border-radius: 4px;">
Mit freundlichen Grüßen
Max Mustermann
Geschäftsführer

Muster GmbH
Musterstraße 123
12345 Musterstadt
Tel: +49 123 456789
E-Mail: max@muster.de
Web: www.muster.de
      </code>

      <p><strong>Signatur verwenden:</strong></p>
      <ul>
        <li><strong>Automatisch:</strong> Die Standard-Signatur wird automatisch in neue E-Mails und Antworten eingefügt</li>
        <li><strong>Manuell auswählen:</strong> Beim Verfassen einer E-Mail können Sie im Dropdown eine andere Signatur wählen</li>
        <li><strong>Keine Signatur:</strong> Wählen Sie "Keine Signatur" wenn Sie keine verwenden möchten</li>
      </ul>

      <p><strong>Signaturen bearbeiten:</strong></p>
      <ul>
        <li>Klicken Sie auf das Stift-Symbol bei der Signatur</li>
        <li>Ändern Sie die gewünschten Felder</li>
        <li>Speichern Sie die Änderungen</li>
      </ul>

      <p><strong>Tipps:</strong></p>
      <ul>
        <li><strong>Mehrere Signaturen:</strong> Erstellen Sie verschiedene Signaturen für unterschiedliche Anlässe</li>
        <li><strong>Rechtliche Pflichtangaben:</strong> Achten Sie auf gesetzliche Anforderungen (z.B. Impressumspflicht)</li>
        <li><strong>Kurz halten:</strong> Signaturen sollten nicht zu lang sein (max. 5-7 Zeilen)</li>
        <li><strong>Kontaktdaten aktuell halten:</strong> Prüfen Sie regelmäßig, ob alle Angaben noch stimmen</li>
        <li><strong>HTML sparsam nutzen:</strong> Nicht alle E-Mail-Clients unterstützen HTML vollständig</li>
      </ul>
   `
  },

  // Adressen
  {
    id: 'adressen-erstellen',
    category: 'adressen',
    title: 'Kontakte erstellen',
    description: 'Neue Adressen und Kontakte anlegen',
    keywords: ['adresse', 'kontakt', 'erstellen', 'neu', 'person', 'firma'],
    order: 1,
    content:`
      <h3>Kontakte erstellen</h3>
      <p><strong>Neuen Kontakt anlegen:</strong></p>
      <ol>
        <li>Gehen Sie zu <strong>"Adressen"</strong></li>
        <li>Klicken Sie auf "Neue Adresse"</li>
        <li>Füllen Sie die Kontaktdaten aus:
          <ul>
            <li><strong>Name:</strong> Vor- und Nachname oder Firmenname</li>
            <li><strong>E-Mail:</strong> E-Mail-Adresse</li>
            <li><strong>Telefon:</strong> Telefonnummer(n)</li>
            <li><strong>Adresse:</strong> Straße, PLZ, Ort, Land</li>
            <li><strong>Typ:</strong> Privat, Geschäftlich, etc.</li>
            <li><strong>Notizen:</strong> Zusätzliche Informationen</li>
          </ul>
        </li>
        <li>Klicken Sie auf "Speichern"</li>
      </ol>
      <p><strong>Tipp:</strong> Kontakte werden automatisch aus E-Mails extrahiert und können direkt gespeichert werden.</p>
   `
  },
  {
    id: 'adressen-verwalten',
    category: 'adressen',
    title: 'Kontakte verwalten',
    description: 'Adressen suchen, bearbeiten und organisieren',
    keywords: ['adresse', 'suchen', 'bearbeiten', 'verwalten', 'organisieren'],
    order: 2,
    content:`
      <h3>Kontakte verwalten</h3>
      <p><strong>Kontakte suchen:</strong></p>
      <ul>
        <li>Nutzen Sie das Suchfeld oberhalb der Liste</li>
        <li>Suche funktioniert über Name, E-Mail, Telefon und Notizen</li>
        <li>Ergebnisse werden in Echtzeit gefiltert</li>
      </ul>
      <p><strong>Kontakt bearbeiten:</strong></p>
      <ul>
        <li>Klicken Sie auf das Stift-Symbol beim Kontakt</li>
        <li>Ändern Sie die gewünschten Felder</li>
        <li>Speichern Sie die Änderungen</li>
      </ul>
      <p><strong>Kontakt löschen:</strong></p>
      <ul>
        <li>Klicken Sie auf das Papierkorb-Symbol</li>
        <li>Bestätigen Sie die Löschung</li>
        <li><strong>Achtung:</strong> Verknüpfte Tickets und E-Mails bleiben erhalten</li>
      </ul>
      <p><strong>Kontakt verwenden:</strong></p>
      <ul>
        <li>Erstellen Sie direkt aus der Adressansicht E-Mails an den Kontakt</li>
        <li>Verknüpfen Sie Tickets mit Kontakten</li>
        <li>Sehen Sie alle zugehörigen E-Mails und Dokumente</li>
      </ul>
   `
  },
  {
    id: 'adressen-import-export',
    category: 'adressen',
    title: 'CSV Import & Export',
    description: 'Kontakte als CSV-Datei exportieren und importieren',
    keywords: ['adresse', 'import', 'export', 'csv', 'datei', 'backup', 'migration'],
    order: 3,
    lastUpdated: '2025-01-14',
    content:`
      <h3>CSV Import & Export</h3>
      <p>Skeena ermöglicht den Export und Import von Adressdaten im CSV-Format (Comma-Separated Values). Dies ist nützlich für Backups, Datenanalyse oder Migration zu anderen Systemen.</p>

      <p><strong>Adressen exportieren:</strong></p>
      <ol>
        <li>Gehen Sie zur <strong>Adressen</strong>-Übersicht</li>
        <li>Klicken Sie auf den Button <strong>"Export CSV"</strong></li>
        <li>Die CSV-Datei wird automatisch heruntergeladen</li>
        <li>Dateiname: <code>Adressen_Export_YYYY-MM-DD.csv</code></li>
      </ol>

      <p><strong>CSV-Format:</strong></p>
      <p>Die exportierte CSV-Datei enthält folgende Spalten (Semikolon-separiert):</p>
      <ul>
        <li><strong>Anrede:</strong> Herr, Frau, etc.</li>
        <li><strong>Titel:</strong> Dr., Prof., etc.</li>
        <li><strong>Vorname:</strong> Vorname der Person</li>
        <li><strong>Nachname:</strong> Nachname der Person</li>
        <li><strong>Firma:</strong> Firmenname</li>
        <li><strong>Strasse:</strong> Straße und Hausnummer</li>
        <li><strong>PLZ:</strong> Postleitzahl</li>
        <li><strong>Ort:</strong> Stadt/Ort</li>
        <li><strong>Land:</strong> Land (Standard: Deutschland)</li>
        <li><strong>Telefon:</strong> Telefonnummer</li>
        <li><strong>Email:</strong> E-Mail-Adresse</li>
        <li><strong>Notizen:</strong> Zusätzliche Informationen</li>
      </ul>

      <p><strong>Adressen importieren:</strong></p>
      <ol>
        <li>Gehen Sie zur <strong>Adressen</strong>-Übersicht</li>
        <li>Klicken Sie auf den Button <strong>"Import CSV"</strong></li>
        <li>Klicken Sie auf <strong>"CSV-Datei auswählen"</strong></li>
        <li>Wählen Sie Ihre CSV-Datei aus (muss das Skeena-Format haben)</li>
        <li>Klicken Sie auf <strong>"Importieren"</strong></li>
        <li>Sie erhalten eine Zusammenfassung:
          <ul>
            <li>Anzahl erfolgreich importierter Adressen</li>
            <li>Anzahl fehlgeschlagener Imports (falls vorhanden)</li>
          </ul>
        </li>
      </ol>

      <p><strong>Wichtige Hinweise:</strong></p>
      <ul>
        <li><strong>Kodierung:</strong> Die CSV-Datei muss UTF-8-kodiert sein</li>
        <li><strong>Trennzeichen:</strong> Skeena verwendet Semikolon (;) als Trennzeichen (deutsche CSV-Norm)</li>
        <li><strong>Header-Zeile:</strong> Die erste Zeile muss die Spaltennamen enthalten</li>
        <li><strong>Duplikate:</strong> Der Import prüft nicht auf Duplikate - diese müssen vorher bereinigt werden</li>
        <li><strong>Mandanten-Zuordnung:</strong> Importierte Adressen werden automatisch Ihrem Mandanten zugeordnet</li>
      </ul>

      <p><strong>Tipps:</strong></p>
      <ul>
        <li><strong>Vorlage erstellen:</strong> Exportieren Sie zunächst eine Adresse, um das Format zu sehen</li>
        <li><strong>Excel-Bearbeitung:</strong> CSV-Dateien können in Excel bearbeitet werden - speichern Sie als "CSV UTF-8 (durch Trennzeichen getrennt)"</li>
        <li><strong>Backup:</strong> Nutzen Sie den Export regelmäßig für Backups Ihrer Kontaktdaten</li>
        <li><strong>Bulk-Import:</strong> Importieren Sie mehrere hundert Adressen auf einmal</li>
      </ul>

      <p><strong>Fehlerbehebung:</strong></p>
      <ul>
        <li><strong>Import schlägt fehl:</strong> Prüfen Sie, ob die Datei das korrekte Format hat (siehe Export-Beispiel)</li>
        <li><strong>Umlaute falsch:</strong> Stellen Sie sicher, dass die Datei UTF-8-kodiert ist</li>
        <li><strong>Fehlende Spalten:</strong> Alle 12 Spalten müssen vorhanden sein (können aber leer sein)</li>
        <li><strong>Semikolon im Text:</strong> Werte mit Semikolons müssen in Anführungszeichen gesetzt werden</li>
      </ul>
   `
  },
  {
    id: 'adressen-intelligente-suche',
    category: 'adressen',
    title: 'Intelligente Adresssuche',
    description: 'Adressen aus internen und externen Quellen suchen und automatisch anlegen',
    keywords: ['adresse', 'suche', 'intelligent', 'autocomplete', 'extern', 'intern', 'tel.search', 'local.ch', 'automatisch', 'anlegen'],
    order: 4,
    lastUpdated: '2025-11-29',
    content:`
      <h3>Intelligente Adresssuche</h3>
      <p>Die intelligente Adresssuche ermöglicht es Ihnen, Adressen sowohl in der Skeena-Datenbank als auch im Internet zu suchen. Externe Adressen werden automatisch in Ihre Kontaktdatenbank übernommen.</p>

      <p><strong>Funktionsweise:</strong></p>
      <ul>
        <li><strong>Kombinierte Suche:</strong> Durchsucht automatisch interne Datenbank und externe Quellen</li>
        <li><strong>Interne Quellen:</strong> Skeena-Adressdatenbank mit semantischer Suche</li>
        <li><strong>Externe Quellen:</strong> tel.search.ch und local.ch (Schweizer Verzeichnisse)</li>
        <li><strong>Auto-Create:</strong> Externe Adressen werden automatisch angelegt</li>
        <li><strong>Duplikat-Erkennung:</strong> Erkennt bereits vorhandene Kontakte (nach Firma+PLZ oder Name+PLZ)</li>
      </ul>

      <p><strong>Verwendung:</strong></p>
      <ol>
        <li>Öffnen Sie ein Ticket oder Dokument, das eine Adresse benötigt</li>
        <li>Beginnen Sie im Adressfeld zu tippen (mindestens 2 Zeichen)</li>
        <li>Die Suche zeigt automatisch Ergebnisse aus beiden Quellen:
          <ul>
            <li><strong>Intern (blau):</strong> Adressen aus Ihrer Skeena-Datenbank</li>
            <li><strong>Extern (grau):</strong> Adressen aus dem Internet</li>
          </ul>
        </li>
        <li>Wählen Sie eine Adresse aus der Liste</li>
        <li>Bei externen Adressen wird diese automatisch in Ihrer Datenbank gespeichert</li>
      </ol>

      <p><strong>Anzeige der Ergebnisse:</strong></p>
      <ul>
        <li><strong>Firmen-Icon:</strong> Zeigt Firmen-Adressen an</li>
        <li><strong>Personen-Icon:</strong> Zeigt Privat-Adressen an</li>
        <li><strong>Match-Prozentsatz:</strong> Zeigt an, wie gut das Ergebnis zur Suche passt</li>
        <li><strong>Quelle:</strong> "Intern" für Skeena-Datenbank, oder Quellenname (tel.search, local.ch)</li>
      </ul>

      <p><strong>Automatische Aktualisierung:</strong></p>
      <p>Wenn Sie eine externe Adresse auswählen und diese bereits in Ihrer Datenbank existiert (Duplikat-Erkennung), werden die folgenden Felder bei Bedarf aktualisiert:</p>
      <ul>
        <li>Telefonnummer (falls neu verfügbar)</li>
        <li>E-Mail-Adresse (falls neu verfügbar)</li>
        <li>Strasse/Hausnummer (bei Änderungen)</li>
      </ul>

      <p><strong>Tipps:</strong></p>
      <ul>
        <li><strong>Firma suchen:</strong> Geben Sie den Firmennamen und optional den Ort ein (z.B. "Swisscom Zürich")</li>
        <li><strong>Person suchen:</strong> Geben Sie Vor- und Nachname ein</li>
        <li><strong>Schnelle Ergebnisse:</strong> Interne Adressen werden priorisiert und erscheinen zuerst</li>
        <li><strong>Keine Duplikate:</strong> Die automatische Erkennung verhindert doppelte Einträge</li>
      </ul>
   `
  },

  // Dokumente
  {
    id: 'dokumente-erstellen',
    category: 'dokumente',
    title: 'Dokumente erstellen',
    description: 'Neue Dokumente mit Vorlagen generieren',
    keywords: ['dokument', 'erstellen', 'vorlage', 'generieren', 'brief'],
    order: 1,
    content:`
      <h3>Dokumente erstellen</h3>
      <p><strong>Dokument aus Vorlage erstellen:</strong></p>
      <ol>
        <li>Gehen Sie zu <strong>"Dokumente"</strong></li>
        <li>Klicken Sie auf "Neues Dokument"</li>
        <li>Wählen Sie eine Vorlage aus</li>
        <li>Füllen Sie die Felder aus:
          <ul>
            <li><strong>Empfänger:</strong> Wählen Sie eine Adresse</li>
            <li><strong>Betreff:</strong> Titel des Dokuments</li>
            <li><strong>Inhalt:</strong> Der Haupttext (kann KI-generiert werden)</li>
            <li><strong>Fußzeile:</strong> Optional zusätzliche Informationen</li>
          </ul>
        </li>
        <li>Vorschau anzeigen</li>
        <li>Als PDF herunterladen oder per E-Mail versenden</li>
      </ol>
      <p><strong>KI-Unterstützung:</strong></p>
      <ul>
        <li>Lassen Sie die KI Texte generieren oder verbessern</li>
        <li>Automatische Anpassung an Vorlagen-Stil</li>
        <li>Rechtschreibung und Grammatik-Prüfung</li>
      </ul>
   `
  },

  // Projekte
  {
    id: 'projekte-erstellen',
    category: 'projekte',
    title: 'Projekte erstellen',
    description: 'Neue Projekte anlegen und konfigurieren',
    keywords: ['projekt', 'erstellen', 'neu', 'meilenstein'],
    order: 1,
    content:`
      <h3>Projekte erstellen</h3>
      <p><strong>Neues Projekt anlegen:</strong></p>
      <ol>
        <li>Gehen Sie zu <strong>"Projekte"</strong></li>
        <li>Klicken Sie auf "Neues Projekt"</li>
        <li>Füllen Sie die Projektdaten aus:
          <ul>
            <li><strong>Name:</strong> Projektbezeichnung</li>
            <li><strong>Beschreibung:</strong> Detaillierte Projektbeschreibung</li>
            <li><strong>Beginn & Ende:</strong> Projektzeitraum</li>
            <li><strong>Status:</strong> Geplant, Aktiv, Pausiert, etc.</li>
            <li><strong>Budget:</strong> Optional Budgetplanung</li>
          </ul>
        </li>
        <li>Klicken Sie auf "Speichern"</li>
      </ol>
      <p><strong>Meilensteine hinzufügen:</strong></p>
      <ul>
        <li>Öffnen Sie das Projekt</li>
        <li>Fügen Sie Meilensteine mit Zieldatum hinzu</li>
        <li>Verknüpfen Sie Tickets mit Meilensteinen</li>
        <li>Verfolgen Sie den Fortschritt</li>
      </ul>
   `
  },

  // Workflows
  {
    id: 'workflows-uebersicht',
    category: 'workflows',
    title: 'Workflow-Übersicht',
    description: 'Einführung in die Workflow-Funktionen von Skeena',
    keywords: ['workflow', 'übersicht', 'prozess', 'ablauf', 'automatisierung'],
    order: 1,
    lastUpdated: '2025-11-17',
    content:`
      <h3>Workflow-Übersicht</h3>
      <p>Workflows in Skeena ermöglichen die Automatisierung und Standardisierung von Arbeitsabläufen. Ein Workflow besteht aus mehreren aufeinanderfolgenden Schritten (Tickets), die nacheinander abgearbeitet werden.</p>

      <p><strong>Was sind Workflows?</strong></p>
      <ul>
        <li><strong>Strukturierte Prozesse:</strong> Definieren Sie mehrstufige Arbeitsabläufe</li>
        <li><strong>Automatisierte Ticket-Erstellung:</strong> Tickets werden automatisch basierend auf Vorlagen erstellt</li>
        <li><strong>Schrittweise Abarbeitung:</strong> Jeder Schritt wird zum Ticket, das erledigt werden muss</li>
        <li><strong>Automatisierungsoptionen:</strong> Zuweisungen, Webhooks, Fristen und Benachrichtigungen</li>
      </ul>

      <p><strong>Zwei Hauptkonzepte:</strong></p>
      <ul>
        <li><strong>Workflow-Vorlagen:</strong> Wiederverwendbare Vorlagen für standardisierte Prozesse</li>
        <li><strong>Aktive Workflows:</strong> Laufende Instanzen mit tatsächlichen Tickets</li>
      </ul>

      <p><strong>Anwendungsbeispiele:</strong></p>
      <ul>
        <li>Kunden-Onboarding-Prozess (Vertrag erstellen → Zugänge einrichten → Schulung)</li>
        <li>Reklamationsbearbeitung (Erfassung → Prüfung → Lösung → Feedback)</li>
        <li>Monatsabschluss (Daten sammeln → Prüfen → Report erstellen → Freigabe)</li>
        <li>Mitarbeiter-Einstellung (Dokumente → IT-Setup → Einarbeitung → Feedback)</li>
      </ul>

      <p><strong>Zugang:</strong> Gehen Sie im Hauptmenü zu <strong>"Workflows"</strong></p>
   `
  },
  {
    id: 'workflows-vorlagen-erstellen',
    category: 'workflows',
    title: 'Workflow-Vorlagen erstellen',
    description: 'Neue Workflow-Vorlagen mit Schritten definieren',
    keywords: ['workflow', 'vorlage', 'erstellen', 'neu', 'template', 'schritte'],
    order: 2,
    lastUpdated: '2025-11-17',
    content:`
      <h3>Workflow-Vorlagen erstellen</h3>
      <p>Eine Workflow-Vorlage definiert einen wiederverwendbaren Prozess mit vordefinierten Schritten.</p>

      <p><strong>Neue Vorlage erstellen:</strong></p>
      <ol>
        <li>Gehen Sie zu <strong>"Workflows"</strong></li>
        <li>Wechseln Sie zum Tab <strong>"Vorlagen"</strong></li>
        <li>Klicken Sie auf <strong>"Neue Vorlage"</strong></li>
        <li>Füllen Sie die Grunddaten aus:
          <ul>
            <li><strong>Name:</strong> Aussagekräftiger Name (z.B. "Kunden-Onboarding")</li>
            <li><strong>Beschreibung:</strong> Kurze Erklärung des Prozesses</li>
          </ul>
        </li>
      </ol>

      <p><strong>Schritte hinzufügen:</strong></p>
      <ol>
        <li>Klicken Sie auf <strong>"Schritt hinzufügen"</strong></li>
        <li>Definieren Sie jeden Schritt:
          <ul>
            <li><strong>Titel:</strong> Kurze Bezeichnung des Schritts</li>
            <li><strong>Was:</strong> Detaillierte Aufgabenbeschreibung</li>
            <li><strong>Wie:</strong> Vorgehensweise zur Erledigung (optional)</li>
            <li><strong>Priorität:</strong> Niedrig, Mittel oder Hoch</li>
          </ul>
        </li>
        <li>Wiederholen Sie für alle Schritte</li>
      </ol>

      <p><strong>Schritte organisieren:</strong></p>
      <ul>
        <li><strong>Reihenfolge ändern:</strong> Verwenden Sie die Pfeile zum Verschieben</li>
        <li><strong>Schritt bearbeiten:</strong> Klicken Sie auf das Stift-Symbol</li>
        <li><strong>Schritt löschen:</strong> Klicken Sie auf das Papierkorb-Symbol</li>
        <li>Die Schrittnummer wird automatisch aktualisiert</li>
      </ul>

      <p><strong>Automatisierungsoptionen pro Schritt:</strong></p>
      <ul>
        <li><strong>Auto-Zuweisung:</strong> Ticket automatisch einem Benutzer zuweisen</li>
        <li><strong>Auto-Webhook:</strong> Webhook bei Erstellung des Schritt-Tickets auslösen</li>
        <li><strong>Auto-Frist:</strong> Frist automatisch setzen (Tage ab Erstellung)</li>
        <li><strong>Auto-Benachrichtigung:</strong> Zugewiesenen Benutzer per E-Mail benachrichtigen</li>
      </ul>

      <p><strong>Vorlage speichern:</strong></p>
      <ul>
        <li>Klicken Sie auf <strong>"Speichern"</strong></li>
        <li>Die Vorlage ist nun in der Liste verfügbar</li>
        <li>Sie kann jederzeit bearbeitet oder ausgeführt werden</li>
      </ul>

      <p><strong>Tipps für gute Vorlagen:</strong></p>
      <ul>
        <li><strong>Klare Schrittnamen:</strong> Jeder Schritt sollte sofort verständlich sein</li>
        <li><strong>Detaillierte Beschreibungen:</strong> Im "Was"-Feld genau erklären, was zu tun ist</li>
        <li><strong>Realistische Prioritäten:</strong> Nicht alle Schritte sind gleich wichtig</li>
        <li><strong>Sinnvolle Reihenfolge:</strong> Abhängigkeiten beachten</li>
        <li><strong>Nicht zu viele Schritte:</strong> 3-10 Schritte sind ideal</li>
      </ul>
   `
  },
  {
    id: 'workflows-vorlagen-bearbeiten',
    category: 'workflows',
    title: 'Workflow-Vorlagen bearbeiten',
    description: 'Bestehende Workflow-Vorlagen anpassen und löschen',
    keywords: ['workflow', 'vorlage', 'bearbeiten', 'editieren', 'ändern', 'löschen'],
    order: 3,
    lastUpdated: '2025-11-17',
    content:`
      <h3>Workflow-Vorlagen bearbeiten</h3>
      <p>Bestehende Workflow-Vorlagen können jederzeit angepasst werden, um Prozesse zu optimieren.</p>

      <p><strong>Vorlage bearbeiten:</strong></p>
      <ol>
        <li>Gehen Sie zu <strong>"Workflows" → "Vorlagen"</strong></li>
        <li>Finden Sie die gewünschte Vorlage in der Liste</li>
        <li>Klicken Sie auf das <strong>Stift-Symbol</strong> (Bearbeiten)</li>
        <li>Der Bearbeitungsdialog öffnet sich mit allen bestehenden Schritten</li>
        <li>Nehmen Sie die gewünschten Änderungen vor</li>
        <li>Klicken Sie auf <strong>"Speichern"</strong></li>
      </ol>

      <p><strong>Mögliche Änderungen:</strong></p>
      <ul>
        <li><strong>Name und Beschreibung:</strong> Anpassen der Grunddaten</li>
        <li><strong>Schritte hinzufügen:</strong> Neue Schritte am Ende oder dazwischen einfügen</li>
        <li><strong>Schritte bearbeiten:</strong> Titel, Beschreibung, Priorität ändern</li>
        <li><strong>Schritte umordnen:</strong> Reihenfolge mit Pfeilen anpassen</li>
        <li><strong>Schritte entfernen:</strong> Nicht mehr benötigte Schritte löschen</li>
        <li><strong>Automatisierung anpassen:</strong> Auto-Zuweisung, Webhooks, Fristen ändern</li>
      </ul>

      <p><strong>Vorlage löschen:</strong></p>
      <ol>
        <li>Klicken Sie auf das <strong>Papierkorb-Symbol</strong> bei der Vorlage</li>
        <li>Bestätigen Sie die Löschung im Dialog</li>
        <li><strong>Achtung:</strong> Gelöschte Vorlagen können nicht wiederhergestellt werden!</li>
      </ol>

      <p><strong>Wichtige Hinweise:</strong></p>
      <ul>
        <li><strong>Bereits laufende Workflows:</strong> Änderungen an Vorlagen wirken sich nur auf neue Workflow-Instanzen aus</li>
        <li><strong>Bestehende Tickets bleiben:</strong> Bereits erstellte Tickets werden nicht verändert</li>
        <li><strong>Versionierung:</strong> Es gibt keine automatische Versionierung - dokumentieren Sie wichtige Änderungen</li>
      </ul>

      <p><strong>Best Practices:</strong></p>
      <ul>
        <li><strong>Feedback sammeln:</strong> Holen Sie Rückmeldung von Nutzern ein</li>
        <li><strong>Iterativ verbessern:</strong> Kleine, kontinuierliche Verbesserungen</li>
        <li><strong>Dokumentieren:</strong> Halten Sie Änderungen in der Beschreibung fest</li>
        <li><strong>Testen:</strong> Nach Änderungen einen Test-Workflow starten</li>
      </ul>
   `
  },
  {
    id: 'workflows-starten',
    category: 'workflows',
    title: 'Workflows starten',
    description: 'Workflow-Vorlagen ausführen und aktive Workflows verwalten',
    keywords: ['workflow', 'starten', 'ausführen', 'erstellen', 'instanz', 'aktiv'],
    order: 4,
    lastUpdated: '2025-11-17',
    content:`
      <h3>Workflows starten</h3>
      <p>Wenn Sie eine Workflow-Vorlage ausführen, wird eine neue Workflow-Instanz mit allen definierten Schritten als Tickets erstellt.</p>

      <p><strong>Workflow aus Vorlage starten:</strong></p>
      <ol>
        <li>Gehen Sie zu <strong>"Workflows" → "Vorlagen"</strong></li>
        <li>Finden Sie die gewünschte Vorlage</li>
        <li>Klicken Sie auf das <strong>Play-Symbol</strong> (Ausführen)</li>
        <li>Optional: Geben Sie einen <strong>benutzerdefinierten Namen</strong> ein</li>
        <li>Klicken Sie auf <strong>"Workflow starten"</strong></li>
      </ol>

      <p><strong>Was passiert beim Start?</strong></p>
      <ul>
        <li>Ein neuer Workflow wird erstellt</li>
        <li>Für jeden Schritt wird automatisch ein Ticket erstellt</li>
        <li>Tickets erhalten Schrittnummer, Titel, Beschreibung und Priorität</li>
        <li>Auto-Zuweisungen werden durchgeführt (falls konfiguriert)</li>
        <li>Auto-Fristen werden berechnet (ab Erstellungsdatum)</li>
        <li>Webhooks werden ausgelöst (falls konfiguriert)</li>
        <li>E-Mail-Benachrichtigungen werden gesendet (falls aktiviert)</li>
        <li>Der erste Schritt wird als "Aktueller Schritt" markiert</li>
      </ul>

      <p><strong>Aktive Workflows einsehen:</strong></p>
      <ol>
        <li>Gehen Sie zu <strong>"Workflows" → "Aktive Workflows"</strong></li>
        <li>Sie sehen alle laufenden Workflow-Instanzen</li>
        <li>Jeder Workflow zeigt:
          <ul>
            <li>Workflow-Name</li>
            <li>Status (Aktiv, Abgeschlossen)</li>
            <li>Aktueller Schritt</li>
            <li>Fortschritt (X von Y Schritten erledigt)</li>
            <li>Erstellungsdatum</li>
          </ul>
        </li>
      </ol>

      <p><strong>Workflow-Details anzeigen:</strong></p>
      <ul>
        <li>Klicken Sie auf einen Workflow</li>
        <li>Sie sehen alle Schritte als Stepper-Komponente</li>
        <li>Erledigte Schritte sind grün markiert</li>
        <li>Der aktuelle Schritt ist hervorgehoben</li>
        <li>Offene Schritte sind grau</li>
      </ul>

      <p><strong>Workflow-Tickets bearbeiten:</strong></p>
      <ul>
        <li>Workflow-Tickets erscheinen in der normalen Ticket-Liste</li>
        <li>Sie sind mit dem Workflow verknüpft</li>
        <li>Beim Öffnen eines Tickets sehen Sie den Workflow-Kontext</li>
        <li>Status-Änderungen wirken sich auf den Workflow aus</li>
      </ul>

      <p><strong>Tipps:</strong></p>
      <ul>
        <li><strong>Benutzerdefinierter Name:</strong> Verwenden Sie sprechende Namen (z.B. "Onboarding Kunde Müller GmbH")</li>
        <li><strong>Mehrere Instanzen:</strong> Sie können die gleiche Vorlage mehrfach starten</li>
        <li><strong>Parallel arbeiten:</strong> Verschiedene Workflows können gleichzeitig laufen</li>
      </ul>
   `
  },
  {
    id: 'workflows-tickets-abarbeiten',
    category: 'workflows',
    title: 'Workflow-Schritte abarbeiten',
    description: 'Tickets in Workflows bearbeiten und nächste Schritte auslösen',
    keywords: ['workflow', 'ticket', 'abarbeiten', 'erledigen', 'nächster', 'schritt'],
    order: 5,
    lastUpdated: '2025-11-17',
    content:`
      <h3>Workflow-Schritte abarbeiten</h3>
      <p>Workflow-Tickets werden wie normale Tickets bearbeitet, sind aber miteinander verknüpft und müssen in der richtigen Reihenfolge erledigt werden.</p>

      <p><strong>Workflow-Ticket erkennen:</strong></p>
      <ul>
        <li>In der Ticket-Liste an der Workflow-Markierung erkennbar</li>
        <li>Beim Öffnen sehen Sie den Workflow-Kontext</li>
        <li>Schrittnummer wird angezeigt (z.B. "Schritt 2 von 5")</li>
        <li>Stepper zeigt alle Schritte des Workflows</li>
      </ul>

      <p><strong>Ticket bearbeiten:</strong></p>
      <ol>
        <li>Öffnen Sie das Ticket aus der Ticket-Liste</li>
        <li>Bearbeiten Sie die Aufgabe wie gewohnt</li>
        <li>Fügen Sie Notizen, Dokumente oder E-Mails hinzu</li>
        <li>Dokumentieren Sie Ihre Arbeit im "Wie"-Feld</li>
      </ol>

      <p><strong>Schritt abschließen:</strong></p>
      <ol>
        <li>Setzen Sie den Ticket-Status auf <strong>"Erledigt"</strong></li>
        <li>Das Erledigungsdatum wird automatisch gesetzt</li>
        <li>Der Workflow erkennt, dass der Schritt abgeschlossen ist</li>
        <li>Der nächste Schritt wird zum "aktuellen Schritt"</li>
      </ol>

      <p><strong>Nächsten Schritt auslösen:</strong></p>
      <p>Wenn ein Schritt als erledigt markiert wird:</p>
      <ul>
        <li>Das System prüft automatisch den nächsten Schritt</li>
        <li>Falls konfiguriert, werden Auto-Aktionen ausgelöst:
          <ul>
            <li>Automatische Zuweisung an nächsten Bearbeiter</li>
            <li>Webhook wird aufgerufen</li>
            <li>E-Mail-Benachrichtigung wird gesendet</li>
          </ul>
        </li>
        <li>Der Workflow-Fortschritt wird aktualisiert</li>
      </ul>

      <p><strong>Workflow-Fortschritt verfolgen:</strong></p>
      <ul>
        <li>Im Ticket-Dialog sehen Sie den visuellen Stepper</li>
        <li>Erledigte Schritte: Grünes Häkchen</li>
        <li>Aktueller Schritt: Hervorgehoben</li>
        <li>Offene Schritte: Ausgegraut</li>
        <li>Erledigungsdatum wird bei jedem Schritt angezeigt</li>
      </ul>

      <p><strong>Workflow abschließen:</strong></p>
      <ul>
        <li>Wenn alle Schritte erledigt sind, ist der Workflow abgeschlossen</li>
        <li>Der Workflow-Status ändert sich zu "Abgeschlossen"</li>
        <li>Der Workflow bleibt zur Nachverfolgung sichtbar</li>
      </ul>

      <p><strong>Wichtige Hinweise:</strong></p>
      <ul>
        <li><strong>Reihenfolge:</strong> Schritte müssen nicht zwingend in Reihenfolge erledigt werden, aber es ist empfohlen</li>
        <li><strong>Überspringen:</strong> Sie können Schritte überspringen, indem Sie sie direkt auf "Erledigt" setzen</li>
        <li><strong>Zurücksetzen:</strong> Ein erledigter Schritt kann wieder geöffnet werden (Status ändern)</li>
        <li><strong>Dokumentation:</strong> Nutzen Sie die Ticket-Felder zur Dokumentation des Fortschritts</li>
      </ul>

      <p><strong>Best Practices:</strong></p>
      <ul>
        <li>Arbeiten Sie Schritte möglichst in der vorgegebenen Reihenfolge ab</li>
        <li>Dokumentieren Sie Ihre Arbeit im "Wie"-Feld</li>
        <li>Nutzen Sie die Kommentar-Funktion für Notizen</li>
        <li>Verknüpfen Sie relevante Dokumente und E-Mails</li>
      </ul>
   `
  },
  {
    id: 'workflows-automatisierung',
    category: 'workflows',
    title: 'Workflow-Automatisierung',
    description: 'Automatische Zuweisungen, Webhooks und Benachrichtigungen',
    keywords: ['workflow', 'automatisierung', 'automation', 'zuweisung', 'webhook', 'benachrichtigung', 'frist'],
    order: 6,
    lastUpdated: '2025-11-17',
    content:`
      <h3>Workflow-Automatisierung</h3>
      <p>Workflow-Vorlagen bieten umfangreiche Automatisierungsoptionen, um manuelle Arbeit zu reduzieren und Prozesse zu beschleunigen.</p>

      <p><strong>Automatische Zuweisung:</strong></p>
      <ul>
        <li>Jeder Schritt kann einem Benutzer automatisch zugewiesen werden</li>
        <li><strong>Konfiguration:</strong>
          <ol>
            <li>Schritt in der Vorlage bearbeiten</li>
            <li>Im Dropdown "Auto-Zuweisung" einen Benutzer auswählen</li>
            <li>Vorlage speichern</li>
          </ol>
        </li>
        <li><strong>Wirkung:</strong> Beim Workflow-Start wird das Ticket automatisch zugewiesen</li>
        <li><strong>Vorteil:</strong> Aufgaben landen direkt beim richtigen Bearbeiter</li>
      </ul>

      <p><strong>Automatische Fristen:</strong></p>
      <ul>
        <li>Fristen werden automatisch basierend auf dem Erstellungsdatum gesetzt</li>
        <li><strong>Konfiguration:</strong>
          <ol>
            <li>Schritt in der Vorlage bearbeiten</li>
            <li>Feld "Auto-Frist (Tage)" ausfüllen (z.B. 3 für 3 Tage)</li>
            <li>Vorlage speichern</li>
          </ol>
        </li>
        <li><strong>Wirkung:</strong> Ticket-Frist = Erstellungsdatum + X Tage</li>
        <li><strong>Vorteil:</strong> Realistische Deadlines ohne manuelle Berechnung</li>
      </ul>

      <p><strong>Automatische Benachrichtigungen:</strong></p>
      <ul>
        <li>Zugewiesene Benutzer werden per E-Mail benachrichtigt</li>
        <li><strong>Konfiguration:</strong>
          <ol>
            <li>Schritt in der Vorlage bearbeiten</li>
            <li>Checkbox "Auto-Benachrichtigung" aktivieren</li>
            <li>Vorlage speichern</li>
          </ol>
        </li>
        <li><strong>Wirkung:</strong> E-Mail wird bei Ticket-Erstellung gesendet</li>
        <li><strong>Vorteil:</strong> Bearbeiter wird sofort informiert</li>
        <li><strong>Voraussetzung:</strong> E-Mail-Konto muss konfiguriert sein</li>
      </ul>

      <p><strong>Webhook-Integration:</strong></p>
      <ul>
        <li>Externe Systeme können automatisch angesprochen werden</li>
        <li><strong>Konfiguration:</strong>
          <ol>
            <li>Webhook in der Webhook-Verwaltung erstellen</li>
            <li>Schritt in der Vorlage bearbeiten</li>
            <li>Im Dropdown "Auto-Webhook" den Webhook auswählen</li>
            <li>Vorlage speichern</li>
          </ol>
        </li>
        <li><strong>Wirkung:</strong> Webhook wird bei Ticket-Erstellung aufgerufen</li>
        <li><strong>Anwendungsfälle:</strong>
          <ul>
            <li>N8N-Workflow auslösen</li>
            <li>Slack/Teams-Nachricht senden</li>
            <li>Externe API aufrufen</li>
            <li>Daten in andere Systeme synchronisieren</li>
          </ul>
        </li>
      </ul>

      <p><strong>Kombination der Automatisierungen:</strong></p>
      <p>Alle Optionen können kombiniert werden:</p>
      <code style="display: block; white-space: pre-wrap; background: #f5f5f5; padding: 10px; border-radius: 4px; margin: 10px 0;">
Schritt 1: "Vertrag erstellen"
- Auto-Zuweisung: Max Mustermann
- Auto-Frist: 3 Tage
- Auto-Benachrichtigung: Ja
- Auto-Webhook: "Slack-Notification"

Schritt 2: "Zugänge einrichten"
- Auto-Zuweisung: IT-Support
- Auto-Frist: 1 Tag
- Auto-Benachrichtigung: Ja
- Auto-Webhook: "IT-Ticket-System"
      </code>

      <p><strong>Best Practices:</strong></p>
      <ul>
        <li><strong>Realistische Fristen:</strong> Setzen Sie erreichbare Zeitrahmen</li>
        <li><strong>Klare Zuweisungen:</strong> Weisen Sie Aufgaben den richtigen Personen zu</li>
        <li><strong>Benachrichtigungen sparsam:</strong> Nur bei wichtigen Schritten aktivieren</li>
        <li><strong>Webhooks testen:</strong> Prüfen Sie Webhook-Funktionalität vor produktivem Einsatz</li>
        <li><strong>Dokumentieren:</strong> Halten Sie die Automatisierungslogik in der Beschreibung fest</li>
      </ul>

      <p><strong>Fehlerbehebung:</strong></p>
      <ul>
        <li><strong>E-Mail wird nicht gesendet:</strong> E-Mail-Konto prüfen</li>
        <li><strong>Webhook schlägt fehl:</strong> URL und Erreichbarkeit prüfen</li>
        <li><strong>Falsche Frist:</strong> Auto-Frist-Wert in Vorlage korrigieren</li>
        <li><strong>Falscher Bearbeiter:</strong> Auto-Zuweisung in Vorlage anpassen</li>
      </ul>
   `
  },
  {
    id: 'workflows-ki-analyse',
    category: 'workflows',
    title: 'KI-basierte Workflow-Analyse',
    description: 'KI-Vorschläge für Workflows basierend auf Ticket-Inhalten',
    keywords: ['workflow', 'ki', 'ai', 'analyse', 'vorschlag', 'automatisch'],
    order: 7,
    lastUpdated: '2025-11-17',
    content:`
      <h3>KI-basierte Workflow-Analyse</h3>
      <p>Skeena kann mithilfe künstlicher Intelligenz automatisch Workflow-Vorschläge basierend auf Ticket-Inhalten generieren.</p>

      <p><strong>Was macht die KI-Analyse?</strong></p>
      <ul>
        <li>Analysiert den Titel und die Beschreibung eines Tickets</li>
        <li>Erkennt, ob das Ticket Teil eines größeren Prozesses sein könnte</li>
        <li>Schlägt passende Workflow-Schritte vor</li>
        <li>Empfiehlt Prioritäten und Reihenfolge</li>
      </ul>

      <p><strong>KI-Analyse auslösen:</strong></p>
      <ol>
        <li>Öffnen Sie ein bestehendes Ticket</li>
        <li>Klicken Sie auf <strong>"Workflow analysieren"</strong></li>
        <li>Die KI analysiert das Ticket</li>
        <li>Sie erhalten einen Workflow-Vorschlag mit:
          <ul>
            <li>Empfohlener Workflow-Name</li>
            <li>Vorgeschlagene Schritte mit Beschreibungen</li>
            <li>Prioritäten für jeden Schritt</li>
          </ul>
        </li>
        <li>Sie können den Vorschlag annehmen oder anpassen</li>
      </ol>

      <p><strong>Beispiel:</strong></p>
      <code style="display: block; white-space: pre-wrap; background: #f5f5f5; padding: 10px; border-radius: 4px; margin: 10px 0;">
Ticket: "Neue Website für Kunde XY erstellen"

KI-Vorschlag:
Workflow: "Website-Erstellung"
1. Anforderungsanalyse (Hoch)
2. Design-Konzept erstellen (Hoch)
3. Technische Umsetzung (Hoch)
4. Content einpflegen (Mittel)
5. Testing und QA (Hoch)
6. Kundenfeedback einholen (Mittel)
7. Finale Anpassungen (Niedrig)
8. Go-Live und Abnahme (Hoch)
      </code>

      <p><strong>Vorschlag übernehmen:</strong></p>
      <ul>
        <li>Klicken Sie auf <strong>"Workflow erstellen"</strong></li>
        <li>Der Workflow wird mit allen Schritten erstellt</li>
        <li>Sie können ihn noch anpassen bevor Sie speichern</li>
        <li>Optional: Als Vorlage speichern für spätere Verwendung</li>
      </ul>

      <p><strong>Voraussetzungen:</strong></p>
      <ul>
        <li>LLM-Provider muss konfiguriert sein</li>
        <li>Benutzer benötigt LLM-Zugang</li>
        <li>Ticket sollte aussagekräftige Beschreibung haben</li>
      </ul>

      <p><strong>Tipps für bessere Ergebnisse:</strong></p>
      <ul>
        <li><strong>Detaillierte Ticket-Beschreibung:</strong> Je mehr Kontext, desto besser der Vorschlag</li>
        <li><strong>Klarer Titel:</strong> Prägnanter Titel hilft der KI</li>
        <li><strong>Prüfen Sie die Vorschläge:</strong> KI-Vorschläge sind Ausgangspunkte, keine finalen Lösungen</li>
        <li><strong>Anpassen:</strong> Passen Sie die Schritte an Ihre spezifischen Bedürfnisse an</li>
      </ul>

      <p><strong>Wann ist KI-Analyse sinnvoll?</strong></p>
      <ul>
        <li>Bei komplexen, mehrstufigen Aufgaben</li>
        <li>Wenn Sie unsicher über die beste Vorgehensweise sind</li>
        <li>Um neue Workflow-Ideen zu generieren</li>
        <li>Als Inspiration für Prozessoptimierung</li>
      </ul>

      <p><strong>Wichtiger Hinweis:</strong> Die KI-Vorschläge basieren auf Best Practices und dem Kontext des Tickets. Sie sollten immer von einem Menschen geprüft und angepasst werden.</p>
   `
  },

  // Einstellungen
  {
    id: 'einstellungen-benutzer',
    category: 'einstellungen',
    title: 'Benutzer-Einstellungen',
    description: 'Persönliche Einstellungen anpassen',
    keywords: ['einstellungen', 'benutzer', 'profil', 'passwort'],
    order: 1,
    content:`
      <h3>Benutzer-Einstellungen</h3>
      <p><strong>Profil bearbeiten:</strong></p>
      <ul>
        <li>Gehen Sie zu <strong>"Meine Einstellungen"</strong></li>
        <li>Ändern Sie Name, E-Mail und Anzeigename</li>
        <li>Speichern Sie die Änderungen</li>
      </ul>
      <p><strong>Passwort ändern:</strong></p>
      <ul>
        <li>In den Benutzer-Einstellungen finden Sie den Bereich "Passwort ändern"</li>
        <li>Geben Sie Ihr aktuelles und neues Passwort ein</li>
        <li>Bestätigen Sie die Änderung</li>
      </ul>
      <p><strong>Standard-Einstellungen:</strong></p>
      <ul>
        <li><strong>Standard-Ort:</strong> Für Dokumenten-Fußzeilen</li>
        <li><strong>Standard-Signatur:</strong> Für ausgehende E-Mails</li>
        <li><strong>Paperless-NGX:</strong> Integration mit Dokumenten-Archiv</li>
      </ul>
   `
  },
  {
    id: 'einstellungen-ki-system-prompts',
    category: 'einstellungen',
    title: 'KI System-Prompts',
    description: 'System-Prompts für KI-Features konfigurieren und anpassen',
    keywords: ['ki', 'ai', 'system', 'prompt', 'llm', 'konfiguration', 'anpassen'],
    order: 2,
    lastUpdated: '2025-01-14',
    content:`
      <h3>KI System-Prompts</h3>
      <p>System-Prompts steuern das Verhalten der KI in verschiedenen Skeena-Funktionen. Sie definieren, wie die KI E-Mails analysiert, Antworten generiert oder Tickets erstellt.</p>

      <p><strong>Was sind System-Prompts?</strong></p>
      <p>Ein System-Prompt ist eine Anweisung an die KI, die definiert:</p>
      <ul>
        <li><strong>Rolle:</strong> Wie soll sich die KI verhalten? (z.B. als professioneller Assistent)</li>
        <li><strong>Kontext:</strong> In welchem Umfeld arbeitet die KI? (z.B. Geschäftskommunikation)</li>
        <li><strong>Regeln:</strong> Welche Richtlinien muss die KI befolgen? (z.B. höflicher Ton, Datenschutz)</li>
        <li><strong>Format:</strong> Wie sollen die Ergebnisse strukturiert sein? (z.B. JSON, Text)</li>
      </ul>

      <p><strong>System-Prompts verwalten:</strong></p>
      <ol>
        <li>Gehen Sie zu <strong>Einstellungen → System-Einstellungen</strong></li>
        <li>Scrollen Sie zum Bereich <strong>"KI System-Prompts"</strong></li>
        <li>Sie sehen verschiedene Prompt-Kategorien:
          <ul>
            <li><strong>E-Mail-Analyse:</strong> Für Kategorisierung und Zusammenfassung</li>
            <li><strong>E-Mail-Antwort:</strong> Für Antwortvorschläge</li>
            <li><strong>Ticket-Analyse:</strong> Für automatische Ticket-Erstellung</li>
            <li><strong>Text-Generierung:</strong> Für Dokumente und Briefe</li>
          </ul>
        </li>
        <li>Klicken Sie auf <strong>"Bearbeiten"</strong> um einen Prompt anzupassen</li>
      </ol>

      <p><strong>Beispiel: E-Mail-Analyse System-Prompt</strong></p>
      <code style="display: block; white-space: pre-wrap; background: #f5f5f5; padding: 10px; border-radius: 4px; margin: 10px 0;">
Du bist ein KI-Assistent für E-Mail-Analyse.

Deine Aufgabe:
- Analysiere eingehende E-Mails
- Kategorisiere sie (Anfrage, Beschwerde, Information, Auftrag, etc.)
- Erstelle eine prägnante Zusammenfassung (2-3 Sätze)
- Identifiziere wichtige Aktionspunkte

Richtlinien:
- Sei objektiv und sachlich
- Konzentriere dich auf die Kernaussage
- Achte auf Dringlichkeit und Priorität
- Datenschutz: Erwähne keine sensiblen Daten in der Zusammenfassung

Antwort-Format: JSON mit Feldern "kategorie", "zusammenfassung", "prioritaet"
      </code>

      <p><strong>System-Prompt anpassen:</strong></p>
      <ol>
        <li>Klicken Sie auf das Stift-Symbol beim gewünschten Prompt</li>
        <li>Bearbeiten Sie den Text im Editor</li>
        <li>Nutzen Sie klare, präzise Anweisungen</li>
        <li>Testen Sie den geänderten Prompt mit Beispiel-Daten</li>
        <li>Klicken Sie auf <strong>"Speichern"</strong></li>
      </ol>

      <p><strong>Best Practices für System-Prompts:</strong></p>
      <ul>
        <li><strong>Klare Rolle definieren:</strong> "Du bist ein professioneller E-Mail-Assistent"</li>
        <li><strong>Konkrete Aufgaben:</strong> Listen Sie genau auf, was die KI tun soll</li>
        <li><strong>Richtlinien setzen:</strong> Definieren Sie Ton, Stil und Einschränkungen</li>
        <li><strong>Format vorgeben:</strong> Geben Sie das gewünschte Ausgabeformat an (JSON, Text, etc.)</li>
        <li><strong>Beispiele nutzen:</strong> Fügen Sie Beispiele für gewünschte Ausgaben hinzu (Few-Shot Learning)</li>
        <li><strong>Sprache:</strong> System-Prompts können in Deutsch oder Englisch verfasst werden</li>
      </ul>

      <p><strong>Erweiterte Anpassungen:</strong></p>
      <ul>
        <li><strong>Unternehmens-Kontext:</strong> Fügen Sie spezifische Informationen über Ihr Unternehmen hinzu</li>
        <li><strong>Branchen-Terminologie:</strong> Verwenden Sie Fachbegriffe Ihrer Branche</li>
        <li><strong>Tonalität:</strong> Definieren Sie, ob formal, freundlich oder direkt kommuniziert werden soll</li>
        <li><strong>Compliance:</strong> Integrieren Sie rechtliche oder regulatorische Anforderungen</li>
      </ul>

      <p><strong>Beispiel für Anpassung (E-Mail-Antwort):</strong></p>
      <code style="display: block; white-space: pre-wrap; background: #f5f5f5; padding: 10px; border-radius: 4px; margin: 10px 0;">
Du bist ein E-Mail-Assistent für die [Ihr Unternehmen] GmbH.

Kontext:
- Wir sind ein mittelständisches IT-Unternehmen
- Unsere Kunden sind Geschäftskunden (B2B)
- Wir legen Wert auf persönlichen, aber professionellen Ton

Aufgabe:
- Erstelle 3 Antwortvorschläge auf eingehende E-Mails
- Variiere die Tonalität: Formal, Freundlich, Direkt
- Beantworte alle Fragen aus der Original-E-Mail
- Füge relevante Informationen hinzu

Richtlinien:
- Sieze den Kunden immer
- Verwende die Anrede "Sehr geehrte/r"
- Unterschrift: Mit freundlichen Grüßen
- Maximal 150 Wörter pro Antwort
- Keine Marketing-Floskeln
- Datenschutz beachten

Antwort-Format: JSON-Array mit Objekten {"stil": "...", "text": "..."}
      </code>

      <p><strong>Testen von System-Prompts:</strong></p>
      <ol>
        <li>Nach dem Anpassen eines Prompts, testen Sie ihn mit realen Daten</li>
        <li>Prüfen Sie, ob die Ergebnisse Ihren Erwartungen entsprechen</li>
        <li>Iterieren Sie: Verfeinern Sie den Prompt basierend auf den Ergebnissen</li>
        <li>Dokumentieren Sie Änderungen für das Team</li>
      </ol>

      <p><strong>Wichtige Hinweise:</strong></p>
      <ul>
        <li><strong>Änderungen wirken sofort:</strong> Neue Prompts werden ab der nächsten KI-Anfrage verwendet</li>
        <li><strong>Backup:</strong> Kopieren Sie den Original-Prompt vor Änderungen</li>
        <li><strong>Zugriffsrechte:</strong> Nur Administratoren können System-Prompts bearbeiten</li>
        <li><strong>LLM-Provider:</strong> Manche Prompts funktionieren besser mit bestimmten KI-Modellen</li>
        <li><strong>Token-Limits:</strong> Sehr lange Prompts verbrauchen mehr Tokens und kosten mehr</li>
      </ul>

      <p><strong>Fehlerbehebung:</strong></p>
      <ul>
        <li><strong>Ungenaue Ergebnisse:</strong> Prompt zu vage - konkretisieren Sie die Anweisungen</li>
        <li><strong>Falsches Format:</strong> Geben Sie explizit das gewünschte Format an (z.B. "Antwort als JSON")</li>
        <li><strong>Zu lange Antworten:</strong> Setzen Sie Längenbeschränkungen ("Maximal 100 Wörter")</li>
        <li><strong>Unpassender Ton:</strong> Definieren Sie Tonalität klar ("Formal und höflich")</li>
      </ul>

      <p><strong>Tipp:</strong> Schauen Sie sich die Standard-Prompts an, um zu verstehen, wie effektive System-Prompts strukturiert sind. Die Skeena-Entwickler haben diese sorgfältig optimiert.</p>
   `
  },

  // Administration
  {
    id: 'admin-mandanten',
    category: 'administration',
    title: 'Mandanten verwalten',
    description: 'Mandanten erstellen und konfigurieren (Admin)',
    keywords: ['admin', 'mandant', 'mandanten', 'verwaltung', 'multi-tenancy'],
    order: 1,
    content:`
      <h3>Mandanten verwalten</h3>
      <p><strong>Was sind Mandanten?</strong></p>
      <p>Mandanten ermöglichen die Verwaltung mehrerer unabhängiger Organisationen in einer Skeena-Instanz. Jeder Mandant hat eigene Benutzer, Daten und Einstellungen.</p>
      <p><strong>Neuen Mandanten erstellen:</strong></p>
      <ol>
        <li>Gehen Sie zu <strong>Administration → Mandanten</strong></li>
        <li>Klicken Sie auf "Neuer Mandant"</li>
        <li>Geben Sie Name und Beschreibung ein</li>
        <li>Aktivieren Sie den Mandanten</li>
        <li>Speichern Sie die Änderungen</li>
      </ol>
      <p><strong>Mandanten-Einstellungen:</strong></p>
      <ul>
        <li>Deaktivieren Sie Mandanten temporär</li>
        <li>Sehen Sie Statistiken (Benutzer, Tickets, E-Mails, etc.)</li>
        <li>Löschen Sie Mandanten (Achtung: Alle Daten werden gelöscht!)</li>
      </ul>
   `
  },
  {
    id: 'admin-benutzer',
    category: 'administration',
    title: 'Benutzer verwalten',
    description: 'Benutzer erstellen und Berechtigungen zuweisen (Admin)',
    keywords: ['admin', 'benutzer', 'rollen', 'rechte', 'verwaltung'],
    order: 2,
    content:`
      <h3>Benutzer verwalten</h3>
      <p><strong>Benutzer-Übersicht:</strong></p>
      <ul>
        <li>Gehen Sie zu <strong>Administration → Benutzer</strong></li>
        <li>Sehen Sie alle Benutzer im System</li>
        <li>Filtern Sie nach Mandant oder Status</li>
      </ul>
      <p><strong>Benutzer bearbeiten:</strong></p>
      <ul>
        <li>Klicken Sie auf das Stift-Symbol beim Benutzer</li>
        <li>Ändern Sie Stammdaten (Name, E-Mail, etc.)</li>
        <li>Aktivieren oder deaktivieren Sie den Account</li>
        <li>Setzen Sie ein neues Passwort</li>
      </ul>
      <p><strong>Mandant zuweisen:</strong></p>
      <ul>
        <li>Weisen Sie Benutzer einem oder mehreren Mandanten zu</li>
        <li>Benutzer sehen nur Daten ihrer Mandanten</li>
      </ul>
      <p><strong>E-Mail-Konto zuweisen:</strong></p>
      <ul>
        <li>Verknüpfen Sie Benutzer mit E-Mail-Konten</li>
        <li>Benutzer können über zugewiesene Konten E-Mails senden</li>
      </ul>
   `
  },
  {
    id: 'admin-webhooks',
    category: 'administration',
    title: 'Webhook-Verwaltung',
    description: 'Webhooks für N8N-Integration konfigurieren (Admin)',
    keywords: ['admin', 'webhook', 'webhooks', 'n8n', 'automation', 'integration'],
    order: 3,
    lastUpdated: '2025-01-12',
    content:`
      <h3>Webhook-Verwaltung</h3>
      <p>Webhooks ermöglichen die Integration von Skeena mit externen Automatisierungs-Tools wie N8N. Sie können Webhooks definieren, die bei bestimmten Ereignissen aufgerufen werden.</p>

      <p><strong>Webhook-Übersicht:</strong></p>
      <ul>
        <li>Gehen Sie zu <strong>Administration → Webhook-Verwaltung</strong></li>
        <li>Sehen Sie alle konfigurierten Webhooks</li>
        <li>Webhooks können aktiviert oder deaktiviert werden</li>
        <li>Nur aktive Webhooks stehen zur Auswahl in Tickets zur Verfügung</li>
      </ul>

      <p><strong>Neuen Webhook erstellen:</strong></p>
      <ol>
        <li>Klicken Sie auf <strong>"Neuer Webhook"</strong></li>
        <li>Füllen Sie die erforderlichen Felder aus:
          <ul>
            <li><strong>Name:</strong> Sprechender Name für den Webhook (z.B. "Ticket-Benachrichtigung")</li>
            <li><strong>Beschreibung:</strong> Optionale Beschreibung der Funktion</li>
            <li><strong>Webhook-URL:</strong> Die vollständige URL des Webhook-Endpunkts (z.B. https://your-n8n.com/webhook/ticket-automation)</li>
            <li><strong>Methode:</strong> HTTP-Methode (POST, GET, PUT, DELETE) - Standard ist POST</li>
            <li><strong>Headers:</strong> Optional JSON-String mit zusätzlichen HTTP-Headers (z.B. {"Authorization": "Bearer token"})</li>
            <li><strong>Aktiv:</strong> Aktivieren oder deaktivieren Sie den Webhook</li>
          </ul>
        </li>
        <li>Klicken Sie auf <strong>"Speichern"</strong></li>
      </ol>

      <p><strong>Webhook bearbeiten:</strong></p>
      <ul>
        <li>Klicken Sie auf das Stift-Symbol beim Webhook</li>
        <li>Ändern Sie die gewünschten Felder</li>
        <li>Speichern Sie die Änderungen</li>
        <li><strong>Achtung:</strong> Änderungen an der URL oder Methode können bestehende Integrationen beeinflussen</li>
      </ul>

      <p><strong>Webhook löschen:</strong></p>
      <ul>
        <li>Klicken Sie auf das Papierkorb-Symbol</li>
        <li>Bestätigen Sie die Löschung</li>
        <li><strong>Hinweis:</strong> Tickets, die diesen Webhook verwenden, werden nicht gelöscht, verlieren aber die Webhook-Verknüpfung</li>
      </ul>

      <p><strong>Webhooks in Tickets verwenden:</strong></p>
      <ol>
        <li>Öffnen Sie ein Ticket zur Bearbeitung</li>
        <li>Aktivieren Sie <strong>"Automatisch ausführen (N8N Integration)"</strong></li>
        <li>Im N8N Automation-Bereich erscheint ein Dropdown <strong>"Webhook auswählen"</strong></li>
        <li>Wählen Sie einen der aktiven Webhooks aus</li>
        <li>Speichern Sie das Ticket</li>
      </ol>

      <p><strong>Webhook-Ausführung:</strong></p>
      <ul>
        <li>Wenn ein Ticket mit Webhook und Automation-Flag gespeichert wird, wird der Webhook aufgerufen</li>
        <li>Die Webhook-URL erhält die Ticket-Daten als JSON im Request-Body</li>
        <li>N8N (oder ein anderes Tool) kann dann automatisch Aktionen ausführen</li>
        <li>Das Ausführungsergebnis wird im Ticket gespeichert und angezeigt</li>
      </ul>

      <p><strong>Best Practices:</strong></p>
      <ul>
        <li><strong>Sprechende Namen:</strong> Verwenden Sie aussagekräftige Namen (z.B. "Slack-Benachrichtigung bei High-Priority")</li>
        <li><strong>Beschreibungen:</strong> Dokumentieren Sie den Zweck jedes Webhooks</li>
        <li><strong>Sicherheit:</strong> Nutzen Sie HTTPS-URLs und setzen Sie Authentication-Headers</li>
        <li><strong>Testen:</strong> Testen Sie neue Webhooks zuerst mit Test-Tickets</li>
        <li><strong>Deaktivieren statt löschen:</strong> Deaktivieren Sie Webhooks temporär, statt sie zu löschen</li>
        <li><strong>Dokumentation:</strong> Halten Sie die Webhook-Konfiguration in N8N/externen Tools synchron</li>
      </ul>

      <p><strong>Beispiel N8N-Integration:</strong></p>
      <ol>
        <li>Erstellen Sie in N8N einen neuen Workflow mit einem Webhook-Trigger</li>
        <li>Kopieren Sie die Webhook-URL aus N8N</li>
        <li>Erstellen Sie in Skeena einen neuen Webhook mit dieser URL</li>
        <li>Konfigurieren Sie in N8N die gewünschten Aktionen (z.B. Slack-Nachricht, E-Mail, etc.)</li>
        <li>Verknüpfen Sie den Webhook mit einem Ticket in Skeena</li>
        <li>Bei Ticket-Ereignissen wird der N8N-Workflow automatisch ausgeführt</li>
      </ol>

      <p><strong>Fehlerbehebung:</strong></p>
      <ul>
        <li><strong>Webhook wird nicht aufgerufen:</strong> Prüfen Sie, ob der Webhook aktiv ist und das Ticket das Automation-Flag hat</li>
        <li><strong>Fehler bei Ausführung:</strong> Überprüfen Sie die Webhook-URL und Netzwerkkonnektivität</li>
        <li><strong>Falsche Daten:</strong> Kontrollieren Sie die HTTP-Methode und Headers</li>
        <li><strong>Timeout:</strong> Stellen Sie sicher, dass der Webhook-Endpunkt erreichbar und performant ist</li>
      </ul>
   `
  },

  // Tipps & Tricks
  {
    id: 'tipps-tastenkombinationen',
    category: 'tipps-tricks',
    title: 'Tastenkombinationen',
    description: 'Nützliche Shortcuts für schnelleres Arbeiten',
    keywords: ['shortcuts', 'tastenkombinationen', 'hotkeys', 'schnell'],
    order: 1,
    content:`
      <h3>Tastenkombinationen</h3>
      <p>Arbeiten Sie effizienter mit diesen Tastenkombinationen:</p>
      <p><strong>Allgemein:</strong></p>
      <ul>
        <li><code>Strg + K</code> - Schnellsuche öffnen (geplant)</li>
        <li><code>F1</code> - Diese Hilfe öffnen</li>
        <li><code>Esc</code> - Dialog schließen</li>
      </ul>
      <p><strong>Navigation:</strong></p>
      <ul>
        <li>Nutzen Sie die Menüleiste auf der linken Seite</li>
        <li>Klicken Sie auf das Skeena-Logo um zum Dashboard zurückzukehren</li>
      </ul>
      <p><strong>Formulare:</strong></p>
      <ul>
        <li><code>Strg + Enter</code> - Formular absenden (wo verfügbar)</li>
        <li><code>Tab</code> - Zum nächsten Feld</li>
        <li><code>Shift + Tab</code> - Zum vorherigen Feld</li>
      </ul>
   `
  },
  {
    id: 'tipps-ki-nutzen',
    category: 'tipps-tricks',
    title: 'KI optimal nutzen',
    description: 'Tipps zur effektiven Nutzung der KI-Features',
    keywords: ['ki', 'ai', 'llm', 'tipps', 'effizient'],
    order: 2,
    content:`
      <h3>KI optimal nutzen</h3>
      <p>Skeena nutzt künstliche Intelligenz an vielen Stellen. So holen Sie das Beste heraus:</p>
      <p><strong>E-Mail-Antworten:</strong></p>
      <ul>
        <li>Lassen Sie die KI mehrere Antwortvorschläge generieren</li>
        <li>Wählen Sie den passendsten aus und passen Sie ihn an</li>
        <li>Die KI lernt aus dem Kontext der E-Mail</li>
      </ul>
      <p><strong>Ticket-Erstellung:</strong></p>
      <ul>
        <li>Die KI kann aus E-Mails automatisch sinnvolle Tickets erstellen</li>
        <li>Prüfen Sie die Vorschläge und passen Sie sie bei Bedarf an</li>
        <li>Titel und Beschreibung werden intelligent extrahiert</li>
      </ul>
      <p><strong>Text-Verbesserung:</strong></p>
      <ul>
        <li>Nutzen Sie die KI um Texte zu verbessern</li>
        <li>Rechtschreibung und Grammatik werden korrigiert</li>
        <li>Der Stil wird professioneller</li>
      </ul>
      <p><strong>Zusammenfassungen:</strong></p>
      <ul>
        <li>Lange E-Mails oder Dokumente können zusammengefasst werden</li>
        <li>Schneller Überblick über wichtige Inhalte</li>
      </ul>
      <p><strong>Tipp:</strong> Die KI-Features benötigen eine konfigurierte LLM-Provider-Einstellung (OpenAI, Anthropic, etc.)</p>
   `
  },
  {
    id: 'tipps-workflow',
    category: 'tipps-tricks',
    title: 'Empfohlener Workflow',
    description: 'Best Practices für die tägliche Arbeit mit Skeena',
    keywords: ['workflow', 'best practices', 'tipps', 'effizient', 'produktiv'],
    order: 3,
    content:`
      <h3>Empfohlener Workflow</h3>
      <p><strong>Morgendliche Routine:</strong></p>
      <ol>
        <li><strong>Dashboard checken:</strong> Verschaffen Sie sich einen Überblick</li>
        <li><strong>E-Mails abrufen:</strong> Neue Nachrichten synchronisieren</li>
        <li><strong>Kalender prüfen:</strong> Heutige und anstehende Fristen ansehen</li>
        <li><strong>Tickets priorisieren:</strong> Wichtigste Aufgaben identifizieren</li>
      </ol>
      <p><strong>E-Mail-Bearbeitung:</strong></p>
      <ol>
        <li>Neue E-Mails durchgehen</li>
        <li>Wichtige E-Mails sofort beantworten (KI-Vorschläge nutzen)</li>
        <li>Aus E-Mails mit Aufgaben direkt Tickets erstellen</li>
        <li>E-Mails mit Kontakten und Projekten verknüpfen</li>
      </ol>
      <p><strong>Ticket-Management:</strong></p>
      <ol>
        <li>Tickets nach Priorität abarbeiten</li>
        <li>Status regelmäßig aktualisieren</li>
        <li>Erledigte Tickets auf "Erledigt" setzen</li>
        <li>Dokumentation im "Wie"-Feld pflegen</li>
      </ol>
      <p><strong>Abend-Routine:</strong></p>
      <ol>
        <li>Offene Tickets für morgen vorbereiten</li>
        <li>Kalender für kommende Woche überprüfen</li>
        <li>Überfällige Tickets identifizieren und neu planen</li>
      </ol>
      <p><strong>Wöchentlich:</strong></p>
      <ul>
        <li>Kontakte aktualisieren und pflegen</li>
        <li>Projekt-Status überprüfen</li>
        <li>Vorlagen und Textbausteine optimieren</li>
        <li>Statistiken analysieren</li>
      </ul>
   `
  },

  // Fristen (Treuhand-Modul)
  {
    id: 'fristen-uebersicht',
    category: 'fristen',
    title: 'Fristen-Dashboard Übersicht',
    description: 'Übersicht über das Fristen-Dashboard für Treuhand-Aufgaben',
    keywords: ['fristen', 'treuhand', 'dashboard', 'übersicht', 'termine', 'deadlines'],
    order: 1,
    lastUpdated: '20.12.2024',
    content: `
      <h3>Das Fristen-Dashboard</h3>
      <p>Das Fristen-Dashboard ist speziell für Treuhand-Aufgaben konzipiert und bietet eine zentrale Übersicht aller Kundenfristen.</p>

      <h3>Statistik-Karten</h3>
      <p>Am oberen Rand sehen Sie wichtige Kennzahlen:</p>
      <ul>
        <li><strong>Überfällig:</strong> Fristen, deren Fälligkeitsdatum überschritten ist (rot markiert)</li>
        <li><strong>Heute:</strong> Fristen, die heute fällig sind</li>
        <li><strong>Diese Woche:</strong> Fristen der nächsten 7 Tage</li>
        <li><strong>Diesen Monat:</strong> Fristen bis Monatsende</li>
        <li><strong>Offen gesamt:</strong> Alle nicht erledigten Fristen</li>
        <li><strong>Erledigt:</strong> Bereits abgeschlossene Fristen</li>
      </ul>
      <p>Klicken Sie auf eine Karte, um nach diesem Zeitraum zu filtern.</p>

      <h3>Ansichten</h3>
      <p>Wechseln Sie zwischen drei Ansichten:</p>
      <ul>
        <li><strong>Liste:</strong> Alle Fristen chronologisch sortiert</li>
        <li><strong>Nach Kunde:</strong> Fristen gruppiert nach Kunden</li>
        <li><strong>Nach Kategorie:</strong> Fristen gruppiert nach Aufgabentyp</li>
      </ul>

      <h3>Suche und Filter</h3>
      <ul>
        <li><strong>Suchfeld:</strong> Suchen Sie nach Titel, Kundenname oder Mandantennummer</li>
        <li><strong>Zeitraum:</strong> Filtern nach Überfällig, Heute, Diese Woche, Diesen Monat</li>
        <li><strong>Kategorie:</strong> Filtern nach Frist-Kategorie</li>
      </ul>
    `
  },
  {
    id: 'fristen-erstellen',
    category: 'fristen',
    title: 'Fristen erstellen und verwalten',
    description: 'Neue Fristen erstellen und bestehende bearbeiten',
    keywords: ['fristen', 'erstellen', 'neu', 'anlegen', 'bearbeiten', 'verwalten'],
    order: 2,
    lastUpdated: '20.12.2024',
    content: `
      <h3>Neue Frist erstellen</h3>
      <p>Fristen werden typischerweise über die Kundendetail-Seite erstellt:</p>
      <ol>
        <li>Öffnen Sie die Adresse/den Kunden</li>
        <li>Wechseln Sie zum Tab "Fristen"</li>
        <li>Klicken Sie auf "Neue Frist"</li>
        <li>Füllen Sie das Formular aus</li>
      </ol>

      <h3>Pflichtfelder</h3>
      <ul>
        <li><strong>Titel:</strong> Kurze Beschreibung der Frist</li>
        <li><strong>Kategorie:</strong> Art der Aufgabe (z.B. Steuererklärung, MWST)</li>
        <li><strong>Fälligkeitsdatum:</strong> Wann muss die Aufgabe erledigt sein?</li>
      </ul>

      <h3>Optionale Felder</h3>
      <ul>
        <li><strong>Beschreibung:</strong> Zusätzliche Details zur Aufgabe</li>
        <li><strong>Priorität:</strong> Normal oder Hoch</li>
        <li><strong>Erinnerungsdatum:</strong> Wann soll erinnert werden?</li>
        <li><strong>Ticket erstellen:</strong> Automatisch ein Ticket für die Frist anlegen</li>
      </ul>

      <h3>Frist als erledigt markieren</h3>
      <p>Im Fristen-Dashboard können Sie Fristen mit dem grünen Häkchen-Button als erledigt markieren. Bei wiederkehrenden Fristen wird automatisch die nächste Frist erstellt.</p>
    `
  },
  {
    id: 'fristen-kategorien',
    category: 'fristen',
    title: 'Fristen-Kategorien',
    description: 'Übersicht der verfügbaren Frist-Kategorien',
    keywords: ['fristen', 'kategorien', 'typen', 'steuererklärung', 'mwst', 'jahresabschluss'],
    order: 3,
    lastUpdated: '20.12.2024',
    content: `
      <h3>Verfügbare Kategorien</h3>
      <p>Jede Frist wird einer Kategorie zugeordnet. Dies hilft bei der Organisation und ermöglicht die Filterung:</p>

      <table>
        <tr><th>Kategorie</th><th>Verwendung</th><th>Kalenderfarbe</th></tr>
        <tr><td><strong>Steuererklärung</strong></td><td>Einkommens-, Vermögens- und Gewinnsteuern</td><td>Rot</td></tr>
        <tr><td><strong>Jahresabschluss</strong></td><td>Bilanzen, Erfolgsrechnungen</td><td>Dunkelrot</td></tr>
        <tr><td><strong>MWST-Abrechnung</strong></td><td>Mehrwertsteuer-Abrechnungen</td><td>Lila</td></tr>
        <tr><td><strong>Sozialversicherung</strong></td><td>AHV, BVG, UVG Meldungen</td><td>Blau</td></tr>
        <tr><td><strong>Lohnabrechnung</strong></td><td>Monatliche Lohnabrechnungen</td><td>Hellblau</td></tr>
        <tr><td><strong>Revision</strong></td><td>Wirtschaftsprüfung, Revisionen</td><td>Grün</td></tr>
        <tr><td><strong>Behörden</strong></td><td>Amtliche Termine und Eingaben</td><td>Orange</td></tr>
        <tr><td><strong>Verträge</strong></td><td>Vertragsfristen und Kündigungen</td><td>Braun</td></tr>
        <tr><td><strong>Allgemein</strong></td><td>Sonstige Fristen</td><td>Orange</td></tr>
      </table>

      <h3>Kalender-Integration</h3>
      <p>Jede Frist erscheint automatisch im Team-Kalender "Fristen" mit der entsprechenden Farbe der Kategorie.</p>
    `
  },
  {
    id: 'fristen-wiederholend',
    category: 'fristen',
    title: 'Wiederkehrende Fristen',
    description: 'Automatische Erstellung wiederkehrender Fristen',
    keywords: ['fristen', 'wiederholend', 'wiederkehrend', 'automatisch', 'intervall', 'monatlich', 'jährlich'],
    order: 4,
    lastUpdated: '20.12.2024',
    content: `
      <h3>Wiederkehrende Fristen einrichten</h3>
      <p>Für regelmässig wiederkehrende Aufgaben können Sie automatische Wiederholungen einrichten:</p>
      <ol>
        <li>Aktivieren Sie "Wiederkehrende Frist" beim Erstellen</li>
        <li>Wählen Sie das Intervall (z.B. 1)</li>
        <li>Wählen Sie die Einheit (z.B. Monat)</li>
      </ol>

      <h3>Verfügbare Intervalle</h3>
      <ul>
        <li><strong>Tag/Tage:</strong> Tägliche Wiederholung</li>
        <li><strong>Woche/Wochen:</strong> Wöchentliche Wiederholung</li>
        <li><strong>Monat/Monate:</strong> Monatliche Wiederholung</li>
        <li><strong>Quartal/Quartale:</strong> Vierteljährliche Wiederholung</li>
        <li><strong>Jahr/Jahre:</strong> Jährliche Wiederholung</li>
      </ul>

      <h3>Automatische Neuerstellung</h3>
      <p>Wenn Sie eine wiederkehrende Frist als erledigt markieren, wird automatisch die nächste Frist mit dem berechneten Datum erstellt. Das Erinnerungsdatum wird entsprechend mit verschoben.</p>

      <h3>Beispiele</h3>
      <ul>
        <li><strong>MWST-Abrechnung:</strong> Intervall 3, Einheit "Monate" (quartalsweise)</li>
        <li><strong>Lohnabrechnung:</strong> Intervall 1, Einheit "Monat" (monatlich)</li>
        <li><strong>Steuererklärung:</strong> Intervall 1, Einheit "Jahr" (jährlich)</li>
      </ul>
    `
  },
  {
    id: 'fristen-erinnerungen',
    category: 'fristen',
    title: 'Erinnerungen und Benachrichtigungen',
    description: 'Frist-Erinnerungen konfigurieren',
    keywords: ['fristen', 'erinnerungen', 'benachrichtigungen', 'vorlauf', 'alarm'],
    order: 5,
    lastUpdated: '20.12.2024',
    content: `
      <h3>Erinnerungen einrichten</h3>
      <p>Jede Frist kann mit einer Erinnerung versehen werden:</p>
      <ol>
        <li>Setzen Sie das <strong>Erinnerungsdatum</strong> beim Erstellen oder Bearbeiten</li>
        <li>Wählen Sie ein Datum vor dem Fälligkeitsdatum</li>
        <li>Die Erinnerung erscheint im Dashboard und Kalender</li>
      </ol>

      <h3>Erinnerungen im Dashboard</h3>
      <p>Fällige Erinnerungen werden im Fristen-Dashboard hervorgehoben. Der Hintergrund-Service <strong>FristenReminderService</strong> prüft täglich um 07:00 Uhr auf fällige Erinnerungen.</p>

      <h3>Kalender-Integration</h3>
      <p>Fristen werden automatisch im Team-Kalender "Fristen" angezeigt. Die Erinnerungszeit wird als Kalender-Erinnerung übernommen.</p>

      <h3>Ticket-Erstellung</h3>
      <p>Optional können Sie bei der Frist-Erstellung ein Ticket erstellen lassen:</p>
      <ul>
        <li>Aktivieren Sie "Ticket erstellen"</li>
        <li>Das Ticket enthält alle Frist-Details</li>
        <li>Das Fälligkeitsdatum wird als Ticket-Datum übernommen</li>
        <li>Der Kunde wird automatisch verknüpft</li>
      </ul>
    `
  },

  // Asset-Verwaltung (Snipe-IT)
  {
    id: 'assets-uebersicht',
    category: 'assets',
    title: 'Asset-Verwaltung Übersicht',
    description: 'IT-Asset-Management mit Snipe-IT Integration',
    keywords: ['assets', 'hardware', 'inventar', 'snipe-it', 'geräte', 'verwaltung'],
    order: 1,
    lastUpdated: '20.12.2024',
    content:`
      <h3>IT-Asset-Management mit Snipe-IT</h3>
      <p>Die Asset-Verwaltung in Skeena ist mit <strong>Snipe-IT</strong> integriert, einem leistungsstarken Open-Source IT-Asset-Management-System. Über diese Integration können Sie:</p>
      <ul>
        <li><strong>Hardware verwalten:</strong> Computer, Monitore, Drucker und andere Geräte</li>
        <li><strong>Lizenzen zuweisen:</strong> Software-Lizenzen an Benutzer oder Geräte</li>
        <li><strong>Zubehör ausgeben:</strong> Tastaturen, Mäuse, Headsets etc.</li>
        <li><strong>Verbrauchsmaterial verfolgen:</strong> Toner, Kabel und andere Materialien</li>
      </ul>
      <h3>Navigation</h3>
      <p>Sie finden die Asset-Verwaltung im Hauptmenü unter <strong>"Assets"</strong>. Die Seite enthält 5 Tabs:</p>
      <ul>
        <li><strong>Assets:</strong> Alle Hardware-Assets (Computer, Monitore, etc.)</li>
        <li><strong>Lizenzen:</strong> Software-Lizenzen mit Sitz-Verwaltung</li>
        <li><strong>Zubehör:</strong> Zubehörteile wie Tastaturen, Headsets</li>
        <li><strong>Verbrauchsmaterial:</strong> Toner, Kabel und andere Verbrauchsartikel</li>
        <li><strong>Benutzer:</strong> Snipe-IT Benutzer und deren zugewiesene Assets</li>
      </ul>
   `
  },
  {
    id: 'assets-checkout',
    category: 'assets',
    title: 'Assets auschecken und einchecken',
    description: 'Geräte an Benutzer ausgeben und zurücknehmen',
    keywords: ['checkout', 'checkin', 'ausgeben', 'zurücknehmen', 'zuweisung'],
    order: 2,
    lastUpdated: '20.12.2024',
    content:`
      <h3>Asset Check-out (Ausgabe)</h3>
      <p>Um ein Asset an einen Benutzer auszugeben:</p>
      <ol>
        <li>Navigieren Sie zu <strong>Assets</strong> und wählen Sie den Tab "Assets"</li>
        <li>Suchen Sie das gewünschte Asset (nutzen Sie die Suchfunktion)</li>
        <li>Klicken Sie auf das <strong>Pfeil-Symbol</strong> in der Aktions-Spalte</li>
        <li>Wählen Sie den Benutzer aus der Liste</li>
        <li>Optional: Fügen Sie eine Notiz und das erwartete Rückgabedatum hinzu</li>
        <li>Klicken Sie auf <strong>"Ausgeben"</strong></li>
      </ol>
      <p><strong>Hinweis:</strong> Nur Assets mit dem Status "Ready to Deploy" können ausgegeben werden.</p>

      <h3>Asset Check-in (Rückgabe)</h3>
      <p>Um ein Asset zurückzunehmen:</p>
      <ol>
        <li>Suchen Sie das zugewiesene Asset in der Liste</li>
        <li>Klicken Sie auf das <strong>Häkchen-Symbol</strong> in der Aktions-Spalte</li>
        <li>Optional: Fügen Sie eine Notiz hinzu</li>
        <li>Klicken Sie auf <strong>"Zurücknehmen"</strong></li>
      </ol>
      <p>Das Asset wird automatisch wieder auf "Ready to Deploy" gesetzt und kann erneut ausgegeben werden.</p>
   `
  },
  {
    id: 'assets-lizenzen',
    category: 'assets',
    title: 'Lizenzen verwalten',
    description: 'Software-Lizenzen und Sitze zuweisen',
    keywords: ['lizenzen', 'software', 'sitze', 'zuweisung', 'license'],
    order: 3,
    lastUpdated: '20.12.2024',
    content:`
      <h3>Lizenzen Übersicht</h3>
      <p>Im Tab "Lizenzen" sehen Sie alle Software-Lizenzen mit:</p>
      <ul>
        <li>Lizenzname und Hersteller</li>
        <li>Produkt-Key (teilweise maskiert)</li>
        <li>Anzahl der freien/gesamten Sitze</li>
        <li>Ablauf- und Kaufdatum</li>
      </ul>

      <h3>Lizenzsitze verwalten</h3>
      <p>Klicken Sie auf das <strong>Auge-Symbol</strong> um die Sitze einer Lizenz zu sehen:</p>
      <ol>
        <li>Ein Dialog zeigt alle Sitze der Lizenz</li>
        <li>Freie Sitze werden mit grünem "Frei" Chip angezeigt</li>
        <li>Belegte Sitze zeigen den zugewiesenen Benutzer/Asset</li>
      </ol>

      <h3>Sitz zuweisen</h3>
      <ol>
        <li>Klicken Sie bei einem freien Sitz auf das Pfeil-Symbol</li>
        <li>Wählen Sie einen Benutzer aus der Liste</li>
        <li>Klicken Sie auf <strong>"Zuweisen"</strong></li>
      </ol>

      <h3>Sitz freigeben</h3>
      <ol>
        <li>Klicken Sie bei einem belegten Sitz auf das Häkchen-Symbol</li>
        <li>Der Sitz wird sofort freigegeben</li>
      </ol>
   `
  },
  {
    id: 'assets-zubehoer',
    category: 'assets',
    title: 'Zubehör und Verbrauchsmaterial',
    description: 'Ausgabe von Zubehör und Verbrauchsartikeln',
    keywords: ['zubehör', 'verbrauchsmaterial', 'tastatur', 'maus', 'toner'],
    order: 4,
    lastUpdated: '20.12.2024',
    content:`
      <h3>Zubehör ausgeben</h3>
      <p>Zubehör wie Tastaturen, Mäuse oder Headsets können mehrfach vorhanden sein:</p>
      <ol>
        <li>Navigieren Sie zum Tab <strong>"Zubehör"</strong></li>
        <li>Die Spalte "Verfügbar/Total" zeigt den aktuellen Bestand</li>
        <li>Klicken Sie auf das Pfeil-Symbol um ein Teil auszugeben</li>
        <li>Wählen Sie den Benutzer und klicken Sie auf "Ausgeben"</li>
      </ol>
      <p><strong>Hinweis:</strong> Bei Zubehör wird der verfügbare Bestand um 1 reduziert.</p>

      <h3>Verbrauchsmaterial</h3>
      <p>Verbrauchsmaterial wie Toner oder Kabel funktioniert ähnlich:</p>
      <ol>
        <li>Navigieren Sie zum Tab <strong>"Verbrauchsmaterial"</strong></li>
        <li>Artikel mit niedrigem Bestand werden mit einem Warnsymbol markiert</li>
        <li>Klicken Sie auf das Pfeil-Symbol zum Ausgeben</li>
      </ol>
      <p><strong>Automatische Warnung:</strong> Bei weniger als 5 Stück erscheint ein rotes Warnsymbol.</p>

      <h3>Bestandsfarben</h3>
      <ul>
        <li><strong>Grün:</strong> Ausreichend Bestand (10+ Stück)</li>
        <li><strong>Orange:</strong> Niedriger Bestand (5-9 Stück)</li>
        <li><strong>Rot:</strong> Kritischer Bestand (unter 5 Stück)</li>
      </ul>
   `
  },
  {
    id: 'assets-konfiguration',
    category: 'assets',
    title: 'Snipe-IT Konfiguration',
    description: 'Einrichtung der Snipe-IT Verbindung',
    keywords: ['konfiguration', 'einrichtung', 'api', 'verbindung', 'snipe-it'],
    order: 5,
    lastUpdated: '20.12.2024',
    content:`
      <h3>Voraussetzungen</h3>
      <p>Für die Asset-Verwaltung benötigen Sie:</p>
      <ul>
        <li>Eine laufende Snipe-IT Installation</li>
        <li>Einen API-Key mit entsprechenden Berechtigungen</li>
        <li>Netzwerkzugriff vom Skeena-Server auf Snipe-IT</li>
      </ul>

      <h3>Konfiguration</h3>
      <p>Die Snipe-IT Verbindung wird in der Datei <code>appsettings.json</code> konfiguriert:</p>
      <pre>
"SnipeIT": {
  "BaseUrl": "http://ihre-snipeit-url",
  "ApiKey": "ihr-api-key"
}
      </pre>

      <h3>API-Key erstellen</h3>
      <ol>
        <li>Melden Sie sich als Admin in Snipe-IT an</li>
        <li>Gehen Sie zu Ihrem Profil → API-Keys verwalten</li>
        <li>Erstellen Sie einen neuen Personal Access Token</li>
        <li>Kopieren Sie den Token in die Konfiguration</li>
      </ol>

      <h3>Fehlerbehebung</h3>
      <p>Falls die Asset-Seite "Snipe-IT nicht konfiguriert" anzeigt:</p>
      <ul>
        <li>Prüfen Sie die BaseUrl (ohne /api/v1 am Ende)</li>
        <li>Prüfen Sie ob der API-Key gültig ist</li>
        <li>Prüfen Sie die Netzwerkverbindung zum Snipe-IT Server</li>
        <li>Starten Sie das Skeena Backend neu nach Konfigurationsänderungen</li>
      </ul>
   `
  },
];

// Helper function to get articles by category
export function getArticlesByCategory(category: HelpCategory): HelpArticle[] {
  return helpArticles
    .filter(article => article.category === category)
    .sort((a, b) => a.order - b.order);
}

// Helper function to search articles
export function searchArticles(query: string): HelpArticle[] {
  const lowerQuery = query.toLowerCase();
  return helpArticles.filter(article =>
    article.title.toLowerCase().includes(lowerQuery) ||
    article.description.toLowerCase().includes(lowerQuery) ||
    article.keywords.some(keyword => keyword.toLowerCase().includes(lowerQuery)) ||
    article.content.toLowerCase().includes(lowerQuery)
  );
}

// Helper function to get category info
export function getCategoryInfo(categoryId: HelpCategory): CategoryInfo | undefined {
  return categories.find(cat => cat.id === categoryId);
}
